import * as THREE from 'three';
import { SURFACES } from '../core/Constants.js';

/**
 * MaterialLibrary - biblioteca de materiais PBR 100% procedurais.
 *
 * Nada aqui vem de arquivo: cada mapa e composto em Float32Array (ruido de valor
 * tileavel, FBM, voronoi, listras analiticas) e sobe para a GPU via canvas 2D.
 *
 * ---------------------------------------------------------------------------
 * CONVENCOES QUE VALEM PARA O ARQUIVO INTEIRO (leia antes de mexer)
 * ---------------------------------------------------------------------------
 * 1. Indice: i = y * S + x. A linha y = 0 e o TOPO da imagem. Como CanvasTexture
 *    nasce com flipY = true, o topo da imagem vira v = 1 (o "alto" da parede).
 *    Isso so importa para o sinal do normal map (item 2) - nenhum material usa
 *    a altura dentro do tile como gradiente de acabamento (ver item 6).
 * 2. Normal map: u cresce com x, e v DECRESCE com y (por causa do flipY). Entao
 *    n = normalize(-dh/dx, +dh/dy, 1). Errar esse sinal inverte relevo (bolha
 *    virando cratera), que e o defeito mais denunciador de textura procedural.
 * 3. As tres texturas por material sao: albedo (sRGB), normal (linear) e ORM
 *    (linear, empacotada como no glTF: R = ambient occlusion, G = roughness,
 *    B = metalness). Uma textura servindo tres mapas economiza banda e memoria.
 * 4. Todo campo de ruido e TILEAVEL: a rede de valor fecha em modulo, o voronoi
 *    considera vizinhos com wrap e as ondas analiticas usam numero inteiro de
 *    periodos por tile. Costura visivel reprova o material inteiro.
 * 5. S e potencia de dois (512), entao "& M" faz o wrap de indice sem custo.
 * 6. NAO EXISTE gradiente vertical monotono assado na textura (do tipo "escuro
 *    embaixo, claro em cima"). Foi tentado e medido: qualquer rampa que vai de
 *    0 a 1 dentro do tile deixa de fechar na emenda e vira uma LINHA horizontal
 *    atravessando a parede toda a cada repeticao - defeito muito mais visivel
 *    que a falta do gradiente. O desgaste vem, no lugar dele, de coisas que
 *    fecham: mancha de ruido de baixa frequencia, escorrido vertical (campo
 *    esticado em Y) e umidade. O encardido concentrado no RODAPE tambem nao
 *    cabe no tile - ele vem da camada de shader (item 7), que enxerga a altura
 *    de mundo e por isso nao repete.
 * 7. CAMADA DE SHADER (onBeforeCompile, ver SURF_LAYER e installSurfaceShader).
 *    O tile carrega so o que e MENOR que ele: poro, agregado, junta, aspereza.
 *    Tudo que e maior que o tile - mancha larga, diferenca de encardido entre
 *    um trecho e outro da parede, faixa suja no rodape - vem de um ruido
 *    amostrado em COORDENADA DE MUNDO (periodo de 5 a 26 m, conforme o
 *    material), que por definicao nao coincide com a emenda do tile. E a mesma
 *    textura, num terceiro nivel de escala, devolve o detalhe de perto (menos
 *    de 5 m) que 512 px por 1,8 m nao tem como carregar.
 *    Se o shader nao puder ser modificado (chunk renomeado em outra versao do
 *    three), a injecao e simplesmente pulada e o material continua valido.
 *
 * ---------------------------------------------------------------------------
 * ESCALA DE MUNDO
 * ---------------------------------------------------------------------------
 * Cada material declara quantos METROS uma repeticao da textura cobre
 * (METERS_PER_TILE). O Level deve pedir a repeticao coerente com o tamanho real
 * da face, senao o tijolo fica do tamanho de uma porta:
 *
 *     const mpt = ctx.materials.metersPerTile('brick');   // 1.32 m
 *     const mat = ctx.materials.getTiled('brick', larguraM / mpt, alturaM / mpt);
 *
 * ---------------------------------------------------------------------------
 * CUSTO
 * ---------------------------------------------------------------------------
 * init() gera tudo UMA vez (alvo: bem abaixo de 1 s). Os campos de ruido pesados
 * sao compartilhados entre materiais e amostrados com deslocamento ciclico
 * proprio de cada um - isso derruba o custo sem criar correlacao visivel. Depois
 * do init os campos sao liberados (~20 MB) para o GC.
 */

// ---------------------------------------------------------------------------
// constantes de modulo
// ---------------------------------------------------------------------------

const TEX_SIZE = 512;                 // potencia de dois: o wrap por mascara depende disso
const TAU = Math.PI * 2;

/** Slots de textura que um material pode ter (usado ao clonar). */
const MAP_SLOTS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap'];

/** Conjunto de superficies validas (Constants.js e a fonte unica). */
const SURFACE_SET = new Set(SURFACES);

/** Metros de mundo cobertos por UMA repeticao da textura. */
const METERS_PER_TILE = {
  concrete: 1.60,
  asphalt: 2.20,
  asphalt_line: 2.20,
  brick: 1.32,          // 6 tijolos de 22 cm x 12 fiadas de 11 cm
  plaster: 1.80,
  plaster_b: 1.80,      // mesma escala fisica: sao a MESMA parede, outra tinta
  plaster_c: 1.80,
  tile: 1.20,           // 4 x 4 placas de 30 cm
  metal: 1.20,
  metal_thin: 2.00,     // 10 ondas de 20 cm
  wood: 0.62,           // 4 tabuas de 15,5 cm
  glass: 1.50,
  dirt: 2.00,
  sand: 1.60,
  gravel: 1.20,
  grass: 1.40,
  fabric: 1.00,
  rubber: 0.90,
  sandbag: 0.70,
  water: 2.40
};

/** Cor chapada de emergencia, se a geracao procedural falhar. */
const FALLBACK_COLOR = {
  concrete: 0x8d8d8a, asphalt: 0x232426, asphalt_line: 0x2a2b2d, brick: 0x6b3a2c,
  plaster: 0xc3b9a4, plaster_b: 0xb0a893, plaster_c: 0xa8a49c,
  tile: 0xb6b3aa, metal: 0x445049, metal_thin: 0x9aa0a4,
  wood: 0x7a5a38, glass: 0xaebfb6, dirt: 0x574430, sand: 0xb59d74,
  gravel: 0x8a8781, grass: 0x4c5a2c, fabric: 0x5e5c42, rubber: 0x1a1a1c,
  sandbag: 0x9a8a63, water: 0x1b2124
};

/**
 * As tres paredes de reboco do mapa. Nao sao a mesma textura com outra tinta:
 * mudam a cor da pintura, QUANTO reboco caiu (cut) e ONDE caiu (ox/oy deslocam
 * a fase dos campos de ruido, entao o desenho da casca e outro). getVariant
 * sorteia entre elas por semente, o que da identidade de material por predio.
 *   r,g,b  cor da tinta em sRGB
 *   cut    limiar da casca: maior = parede mais inteira
 *   wash   peso do escorrido de chuva
 *   ox,oy  deslocamento ciclico dos campos compartilhados
 */
const PLASTER_KINDS = [
  { r: 0.625, g: 0.592, b: 0.526, cut: 0.640, wash: 1.00, ox: 173, oy: 233 },
  { r: 0.505, g: 0.516, b: 0.512, cut: 0.700, wash: 1.30, ox: 61, oy: 347 },
  { r: 0.585, g: 0.472, b: 0.372, cut: 0.600, wash: 0.80, ox: 401, oy: 109 }
];

/**
 * Nome do material -> superficie de SURFACES. So precisa entrar aqui quem tem
 * nome diferente da superficie (variantes de textura e a faixa de asfalto).
 */
const NAME_SURFACE = {
  asphalt_line: 'asphalt',
  plaster_b: 'plaster',
  plaster_c: 'plaster'
};

/**
 * Parametros da camada de shader, por material (ver nota 7 do cabecalho):
 *   [0] macroAmt      amplitude da mancha de mundo no albedo (0..1)
 *   [1] macroMetros   periodo da mancha, em metros de mundo
 *   [2] detalheAmt    forca da perturbacao de normal a menos de 5 m
 *   [3] detalheMetros periodo do detalhe, em metros de mundo
 *   [4] encardidoAmt  forca da sujeira acumulada na base de superficie VERTICAL
 *   [5] encardidoAlt  altura em que o encardido de base morre, em metros
 *   [6] roughNear     piso de rugosidade perto (metal fino usa para anti-alias)
 *   [7] roughFar      piso de rugosidade longe
 *   [8] kind          1 ativa variacao cromatica de tecido em coordenada de mundo
 * Vidro e agua ficam de fora de proposito: mancha e poeira ali sao do material.
 */
const SURF_LAYER = {
  concrete:     [0.34, 19.0, 0.55, 0.24, 0.85, 1.70],
  asphalt:      [0.32, 26.0, 0.42, 0.22, 0.00, 1.00],
  asphalt_line: [0.32, 26.0, 0.42, 0.22, 0.00, 1.00],
  brick:        [0.26, 23.0, 0.45, 0.22, 0.80, 1.45],
  plaster:      [0.36, 15.0, 0.52, 0.24, 0.90, 1.65],
  plaster_b:    [0.36, 17.5, 0.52, 0.24, 0.90, 1.65],
  plaster_c:    [0.36, 12.5, 0.52, 0.24, 0.90, 1.65],
  tile:         [0.24, 12.0, 0.38, 0.20, 0.55, 1.10],
  metal:        [0.22, 11.0, 0.35, 0.18, 0.60, 1.20],
  metal_thin:   [0.20, 14.0, 0.30, 0.20, 0.50, 1.20, 0.45, 0.58],
  wood:         [0.24, 9.00, 0.40, 0.16, 0.55, 0.90],
  dirt:         [0.32, 22.0, 0.40, 0.24, 0.00, 1.00],
  sand:         [0.30, 24.0, 0.38, 0.22, 0.00, 1.00],
  gravel:       [0.30, 20.0, 0.40, 0.22, 0.00, 1.00],
  grass:        [0.30, 16.0, 0.35, 0.20, 0.00, 1.00],
  fabric:       [0.20, 7.00, 0.30, 0.14, 0.35, 0.60, 0.00, 0.00, 1.00],
  rubber:       [0.16, 6.00, 0.30, 0.14, 0.00, 1.00],
  sandbag:      [0.22, 5.00, 0.32, 0.14, 0.55, 0.55]
};

// ---------------------------------------------------------------------------
// matematica basica
// ---------------------------------------------------------------------------

function clamp01(v) {
  return v < 0 ? 0 : (v > 1 ? 1 : v);
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

/** Aceita e0 > e1 (rampa invertida) de proposito - varios materiais usam. */
function smoothstep(e0, e1, x) {
  const d = e1 - e0;
  const t = clamp01(d === 0 ? (x < e0 ? 0 : 1) : (x - e0) / d);
  return t * t * (3 - 2 * t);
}

/** PRNG deterministico: dois builds seguidos geram exatamente a mesma textura. */
function mulberry32(seed) {
  let a = seed >>> 0;
  return function rng() {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash inteiro 2D estavel - usado para dar identidade a cada tijolo/tabua/pedra. */
function hash2i(x, y, seed) {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(seed | 0, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function hashString(str) {
  let h = 2166136261;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// ---------------------------------------------------------------------------
// campos de ruido tileaveis
// ---------------------------------------------------------------------------

/**
 * Soma uma oitava de ruido de valor periodico em out[].
 * fx/fy sao a quantidade de celulas da rede em cada eixo - INTEIROS, e e isso
 * que garante o fechamento nas bordas (em x = S a fase volta a ser a de x = 0).
 * As tabelas de indice/peso sao pre-calculadas por eixo: o laco interno fica
 * com 4 leituras e 3 interpolacoes, que e o que torna viavel gerar tudo isto
 * em tempo de carregamento.
 */
function addValueOctave(out, S, fx, fy, amp, rng) {
  const lat = new Float32Array(fx * fy);
  for (let i = 0; i < lat.length; i++) lat[i] = rng();

  const ix0 = new Int32Array(S), ix1 = new Int32Array(S), wx = new Float32Array(S);
  const iy0 = new Int32Array(S), iy1 = new Int32Array(S), wy = new Float32Array(S);

  for (let x = 0; x < S; x++) {
    const p = (x * fx) / S;
    const c = Math.floor(p);
    const t = p - c;
    ix0[x] = ((c % fx) + fx) % fx;
    ix1[x] = (ix0[x] + 1) % fx;
    wx[x] = t * t * (3 - 2 * t);
  }
  for (let y = 0; y < S; y++) {
    const p = (y * fy) / S;
    const c = Math.floor(p);
    const t = p - c;
    iy0[y] = ((c % fy) + fy) % fy;
    iy1[y] = (iy0[y] + 1) % fy;
    wy[y] = t * t * (3 - 2 * t);
  }

  for (let y = 0; y < S; y++) {
    const r0 = iy0[y] * fx, r1 = iy1[y] * fx, fy0 = wy[y];
    const row = y * S;
    for (let x = 0; x < S; x++) {
      const a = lat[r0 + ix0[x]], b = lat[r0 + ix1[x]];
      const c = lat[r1 + ix0[x]], d = lat[r1 + ix1[x]];
      const f = wx[x];
      const top = a + (b - a) * f;
      const bot = c + (d - c) * f;
      out[row + x] += (top + (bot - top) * fy0) * amp;
    }
  }
}

/** FBM normalizado em 0..1. fx != fy gera ruido esticado (manchas escorridas). */
function fbm(S, fx, fy, octaves, gain, rng) {
  const out = new Float32Array(S * S);
  let amp = 1, total = 0, cx = fx, cy = fy;
  for (let o = 0; o < octaves; o++) {
    addValueOctave(out, S, Math.max(1, Math.round(cx)), Math.max(1, Math.round(cy)), amp, rng);
    total += amp;
    amp *= gain;
    cx *= 2;
    cy *= 2;
  }
  const inv = 1 / total;
  for (let i = 0; i < out.length; i++) out[i] *= inv;
  return out;
}

/** Ruido cristalizado: 1 - |2n-1| vira cordilheira fina - base de trinca. */
function ridged(src) {
  const out = new Float32Array(src.length);
  for (let i = 0; i < src.length; i++) {
    const v = 1 - Math.abs(src[i] * 2 - 1);
    out[i] = v * v;
  }
  return out;
}

/**
 * Voronoi tileavel. Devolve:
 *   d1  distancia normalizada ao ponto mais proximo (0 no centro da celula)
 *   d2  (F2 - F1) normalizado: proximo de 0 exatamente em cima da fronteira
 *   id  valor aleatorio constante por celula (cor/altura por pedra)
 */
function voronoi(S, cells, rng) {
  const n = cells * cells;
  const px = new Float32Array(n), py = new Float32Array(n), pid = new Float32Array(n);
  const cs = S / cells;
  for (let j = 0; j < cells; j++) {
    for (let i = 0; i < cells; i++) {
      const k = j * cells + i;
      px[k] = (i + 0.15 + rng() * 0.7) * cs;
      py[k] = (j + 0.15 + rng() * 0.7) * cs;
      pid[k] = rng();
    }
  }
  const d1 = new Float32Array(S * S);
  const d2 = new Float32Array(S * S);
  const id = new Float32Array(S * S);
  const invCs = 1 / cs;
  for (let y = 0; y < S; y++) {
    const cj = Math.min(cells - 1, Math.floor(y * invCs));
    const row = y * S;
    for (let x = 0; x < S; x++) {
      const ci = Math.min(cells - 1, Math.floor(x * invCs));
      let b1 = 1e18, b2 = 1e18, bid = 0;
      for (let jj = -1; jj <= 1; jj++) {
        let j = cj + jj, oy = 0;
        if (j < 0) { j += cells; oy = -S; } else if (j >= cells) { j -= cells; oy = S; }
        const jrow = j * cells;
        for (let ii = -1; ii <= 1; ii++) {
          let i2 = ci + ii, ox = 0;
          if (i2 < 0) { i2 += cells; ox = -S; } else if (i2 >= cells) { i2 -= cells; ox = S; }
          const k = jrow + i2;
          const dx = px[k] + ox - x;
          const dy = py[k] + oy - y;
          const d = dx * dx + dy * dy;
          if (d < b1) { b2 = b1; b1 = d; bid = pid[k]; } else if (d < b2) { b2 = d; }
        }
      }
      const i3 = row + x;
      const s1 = Math.sqrt(b1);
      d1[i3] = s1 * invCs;
      d2[i3] = (Math.sqrt(b2) - s1) * invCs;
      id[i3] = bid;
    }
  }
  return { d1, d2, id };
}

/** Box blur separavel com wrap (soma corrente: custo O(n), independe do raio). */
function boxBlurWrap(src, S, r, tmp, dst) {
  const M = S - 1;
  const inv = 1 / (2 * r + 1);
  for (let y = 0; y < S; y++) {
    const row = y * S;
    let sum = 0;
    for (let k = -r; k <= r; k++) sum += src[row + (k & M)];
    for (let x = 0; x < S; x++) {
      tmp[row + x] = sum * inv;
      sum += src[row + ((x + r + 1) & M)] - src[row + ((x - r) & M)];
    }
  }
  for (let x = 0; x < S; x++) {
    let sum = 0;
    for (let k = -r; k <= r; k++) sum += tmp[(k & M) * S + x];
    for (let y = 0; y < S; y++) {
      dst[y * S + x] = sum * inv;
      sum += tmp[(((y + r + 1) & M) * S) + x] - tmp[(((y - r) & M) * S) + x];
    }
  }
  return dst;
}

// ---------------------------------------------------------------------------
// desenho em canvas 2D (riscos, fibras, folhas de grama)
// ---------------------------------------------------------------------------

function newCanvas(S) {
  const c = document.createElement('canvas');
  c.width = S;
  c.height = S;
  return c;
}

/**
 * Rasteriza tracos curtos e devolve o RGBA cru. Cada traco e redesenhado nos
 * vizinhos deslocados de +-S apenas quando encosta na borda - e assim que o
 * risco continua do outro lado sem custo de 9 desenhos por traco.
 * O canal R e cobertura; G e B carregam variacao por traco (tom e profundidade).
 */
function drawStrokes(S, count, rng, cfg) {
  const canvas = newCanvas(S);
  const g = canvas.getContext('2d', { willReadFrequently: true });
  g.fillStyle = '#000000';
  g.fillRect(0, 0, S, S);
  g.lineCap = cfg.cap || 'round';
  for (let n = 0; n < count; n++) {
    const x0 = rng() * S;
    const y0 = rng() * S;
    const ang = cfg.angle ? cfg.angle(rng) : rng() * TAU;
    const len = cfg.min + rng() * (cfg.max - cfg.min);
    const x1 = x0 + Math.cos(ang) * len;
    const y1 = y0 + Math.sin(ang) * len;
    const bx = (rng() - 0.5) * (cfg.bend || 0);
    const by = (rng() - 0.5) * (cfg.bend || 0);
    const lw = cfg.width * (0.45 + rng() * 1.1);
    g.lineWidth = lw;
    g.globalAlpha = cfg.alpha ? cfg.alpha(rng) : 1;
    g.strokeStyle = cfg.color ? cfg.color(rng) : '#ffffff';
    const pad = lw + Math.abs(bx) + Math.abs(by) + 2;
    const minX = Math.min(x0, x1) - pad, maxX = Math.max(x0, x1) + pad;
    const minY = Math.min(y0, y1) - pad, maxY = Math.max(y0, y1) + pad;
    for (let oy = -1; oy <= 1; oy++) {
      const sy = oy * S;
      if (minY + sy > S || maxY + sy < 0) continue;
      for (let ox = -1; ox <= 1; ox++) {
        const sx = ox * S;
        if (minX + sx > S || maxX + sx < 0) continue;
        g.beginPath();
        g.moveTo(x0 + sx, y0 + sy);
        if (cfg.bend) {
          g.quadraticCurveTo((x0 + x1) * 0.5 + bx + sx, (y0 + y1) * 0.5 + by + sy, x1 + sx, y1 + sy);
        } else {
          g.lineTo(x1 + sx, y1 + sy);
        }
        g.stroke();
      }
    }
  }
  return g.getImageData(0, 0, S, S).data;
}

/** Extrai um canal do RGBA cru como campo 0..1. */
function channelField(rgba, S, offset) {
  const out = new Float32Array(S * S);
  for (let i = 0; i < out.length; i++) out[i] = rgba[i * 4 + offset] / 255;
  return out;
}

/**
 * Rede de trincas DESENHADA - e o unico jeito de ter trinca que parece trinca.
 *
 * NAO use limiar sobre ruido "ridged" para isso: cortar 1-|2n-1| perto da crista
 * devolve o CONJUNTO DE NIVEL do ruido de base, ou seja uma curva de nivel de
 * mapa topografico - laco fechado, largura constante, sem inicio, sem ponta e
 * sem bifurcacao. Foi medido em tela: o piso vira rabisco de caneta.
 *
 * Trinca de verdade nasce num ponto, caminha, BIFURCA em Y e AFINA ate sumir.
 * Aqui cada ramo e um caminho de passos curtos com direcao perturbada; a largura
 * cai ate o traco ficar sub-pixel (o antialias do canvas faz o desvanecimento da
 * ponta sozinho) e cada ramo pode gerar ate um filho, ate a segunda geracao.
 *
 * Tileavel: cada segmento so e redesenhado nos vizinhos +-S quando encosta na
 * borda. O tom por ramo vai no strokeStyle com globalAlpha = 1 de proposito -
 * com alpha < 1 as juntas entre segmentos consecutivos somariam cobertura e a
 * trinca sairia "contada", com no em cada passo.
 */
function drawCrackField(S, count, rng, cfg) {
  const canvas = newCanvas(S);
  const g = canvas.getContext('2d', { willReadFrequently: true });
  g.fillStyle = '#000000';
  g.fillRect(0, 0, S, S);
  g.lineCap = 'round';
  g.globalAlpha = 1;

  const wBase = cfg.width ?? 1.5;
  const lenMin = cfg.min ?? 40;
  const lenMax = cfg.max ?? 200;
  const wander = cfg.wander ?? 0.42;
  const branchP = cfg.branch ?? 0.75;

  // fila plana (x, y, angulo, comprimento, largura, geracao, tom). Cresce
  // durante o laco: e assim que o ramo filho entra na fila sem recursao.
  const q = [];
  for (let n = 0; n < count; n++) {
    q.push(rng() * S, rng() * S, rng() * TAU,
           lenMin + rng() * (lenMax - lenMin),
           wBase * (0.55 + rng() * 0.9), 0, 0.55 + rng() * 0.45);
  }

  for (let head = 0; head < q.length; head += 7) {
    let x = q[head], y = q[head + 1], a = q[head + 2];
    const len = q[head + 3], w0 = q[head + 4], gen = q[head + 5], tone = q[head + 6];
    const steps = Math.max(3, Math.round(len / 4.5));
    const step = len / steps;
    const branchAt = (gen < 2 && rng() < branchP) ? 1 + ((rng() * (steps - 2)) | 0) : -1;
    const v = (tone * 255) | 0;
    g.strokeStyle = `rgb(${v},${v},${v})`;
    for (let s = 0; s < steps; s++) {
      const t = (s + 1) / steps;
      const taper = 1 - t * t * t;                 // a ponta afina ate sumir
      const nx = x + Math.cos(a) * step;
      const ny = y + Math.sin(a) * step;
      const lw = Math.max(0.28, w0 * taper);
      g.lineWidth = lw;
      const pad = lw + 2;
      const minX = Math.min(x, nx) - pad, maxX = Math.max(x, nx) + pad;
      const minY = Math.min(y, ny) - pad, maxY = Math.max(y, ny) + pad;
      for (let oy = -1; oy <= 1; oy++) {
        const sy = oy * S;
        if (minY + sy > S || maxY + sy < 0) continue;
        for (let ox = -1; ox <= 1; ox++) {
          const sx = ox * S;
          if (minX + sx > S || maxX + sx < 0) continue;
          g.beginPath();
          g.moveTo(x + sx, y + sy);
          g.lineTo(nx + sx, ny + sy);
          g.stroke();
        }
      }
      if (s === branchAt) {
        q.push(nx, ny, a + (rng() < 0.5 ? -1 : 1) * (0.55 + rng() * 0.6),
               len * (0.28 + rng() * 0.38), w0 * 0.62, gen + 1, tone * 0.9);
      }
      x = nx; y = ny;
      a += (rng() - 0.5) * wander;
    }
  }
  return g.getImageData(0, 0, S, S).data;
}

// ---------------------------------------------------------------------------
// tijolo compartilhado (o reboco descascado mostra tijolo por baixo)
// ---------------------------------------------------------------------------

const BRICK_OUT = { r: 0, g: 0, b: 0, h: 0, ro: 0, face: 0 };

/**
 * Amostra uma fiada de tijolos em amarracao corrida no ponto (x,y).
 * rows precisa ser PAR para a alternancia de meia-fiada fechar no tile.
 */
function sampleBrick(x, y, i, S, F, cols, rows, mortarPx, out) {
  const bw = S / cols, bh = S / rows;
  const brow = Math.floor(y / bh);
  // fiada barrigada: a linha inteira sobe/desce um pouco. O deslocamento fica
  // ABAIXO da largura da junta de proposito - assim a junta engorda de um lado
  // e afina do outro (que e o que o pedreiro faz), sem abrir buraco nem mover a
  // troca de tom do tijolo para fora da argamassa.
  const vIn = y / bh - brow + (hash2i(0, brow, 9) - 0.5) * 0.05;
  const fx = x + ((brow & 1) ? bw * 0.5 : 0);
  let bcol = Math.floor(fx / bw);
  const uIn = fx / bw - bcol;
  bcol = bcol % cols;

  // ~4.5% dos vaos sao REMENDO: tijolo que caiu e o buraco foi fechado com
  // argamassa nova, mais clara e mais lisa. E o que quebra a leitura de grade.
  const repair = hash2i(bcol, brow, 613) > 0.955 ? 1 : 0;

  const dxp = Math.min(uIn, 1 - uIn) * bw;
  const dyp = Math.min(vIn, 1 - vIn) * bh;
  // a junta nunca e reta: a onda media entorta o contorno, o ruido fino serrilha
  const d = Math.min(dxp, dyp) - (F.mid[i] - 0.5) * 3.6 - (F.fine[i] - 0.5) * 1.5;
  const mortarJitter = (hash2i(0, brow, 313) - 0.5) * 1.2;
  const missing = hash2i(bcol, brow, 947) > 0.985 ? 1 : 0;
  let face = smoothstep(mortarPx + mortarJitter, mortarPx + mortarJitter + 3.4, d)
           * (1 - repair * 0.90) * (1 - missing);

  const toneHash = hash2i(bcol, brow, 7);
  const tone = clamp01(toneHash * 0.72 + F.low[i] * 0.28);
  const soot = hash2i(bcol, brow, 91);
  // profundidade POR TIJOLO: uns assentam para dentro, outros saltam. Sem isto
  // a parede longa vira uma grade matematicamente perfeita.
  const seat = (hash2i(bcol, brow, 401) - 0.5) * 0.16;
  let br, bg, bb;
  if (tone > 0.955) {          // tijolo mal cozido, raro
    br = 0.525; bg = 0.360; bb = 0.278;
  } else if (tone < 0.045) {   // tijolo requeimado, raro
    br = 0.255; bg = 0.145; bb = 0.125;
  } else {
    const t = (tone - 0.045) / 0.91;
    br = 0.365 + t * 0.110;
    bg = 0.175 + t * 0.062;
    bb = 0.135 + t * 0.042;
  }
  // fuligem em alguns tijolos e gradiente interno (o tijolo nao e cor chapada)
  const inner = 1 - soot * 0.22 - (0.5 - Math.abs(vIn - 0.5)) * 0.10;
  const grit = (F.fine[i] - 0.5) * 0.09 + (F.micro[i] - 0.5) * 0.06;
  br = br * inner + grit;
  bg = bg * inner + grit * 0.95;
  bb = bb * inner + grit * 0.9;

  // lasca de canto: onde o tijolo ja esta perto da junta e o ruido concorda
  const chip = smoothstep(0.66, 0.86, F.mid[i]) * smoothstep(mortarPx + 10, mortarPx + 2, d) * (soot > 0.62 ? 1 : 0);
  face *= 1 - chip * 0.90;
  br = lerp(br, br * 1.35 + 0.10, chip);
  bg = lerp(bg, bg * 1.30 + 0.09, chip);
  bb = lerp(bb, bb * 1.28 + 0.08, chip);

  // argamassa mais escura que a ceramica; falha de peca abre um vao real
  const mg = (0.480 + (F.fine[i] - 0.5) * 0.11 + (F.micro[i] - 0.5) * 0.08
             - smoothstep(0.5, 0.9, F.mid[i]) * 0.06) * (1 + repair * 0.07) * (1 - missing * 0.48);
  const tiltU = (hash2i(bcol, brow, 733) - 0.5) * 0.20;
  const tiltV = (hash2i(bcol, brow, 811) - 0.5) * 0.20;
  const tilt = (uIn - 0.5) * tiltU + (vIn - 0.5) * tiltV;
  out.r = lerp(mg * 1.01, br, face);
  out.g = lerp(mg * 0.99, bg, face);
  out.b = lerp(mg * 0.94, bb, face);
  out.h = lerp(0.26 + (F.fine[i] - 0.5) * 0.10 + repair * 0.06 - missing * 0.12,
               0.74 + seat + tilt + (F.micro[i] - 0.5) * 0.09 - chip * 0.22, face);
  out.ro = lerp(0.96 - repair * 0.09, 0.845 + (F.micro[i] - 0.5) * 0.08 + chip * 0.08, face);
  out.face = face;
  return out;
}

// ---------------------------------------------------------------------------
// camada de shader compartilhada (nota 7 do cabecalho)
//
// O tile de 512 px cobre de 1,2 a 2,2 m: tudo que for MAIOR que isso repete de
// forma legivel, e tudo que for menor que 4 mm nao cabe. Estas tres linhas de
// GLSL resolvem os dois lados sem textura nova por material:
//   - macro: ruido amostrado em coordenada de MUNDO (12 a 26 m). Como a fase e
//     a do mundo e nao a do tile, a mancha grande nunca cai na emenda.
//   - encardido de contato: so onde a superficie e VERTICAL (normal geometrica
//     tirada da derivada da posicao de mundo) e so perto do chao.
//   - detalhe: a mesma textura em escala de 15 a 25 cm perturbando a normal,
//     com peso que morre a 5,5 m - e o que sobra para a camera a meio metro.
// A projecao (x+z, 1.15y+0.6z) e inversivel para chao E para parede em
// qualquer um dos dois eixos, entao uma amostra so serve as tres orientacoes.
// ---------------------------------------------------------------------------

const SURF_VERT_DECL = 'varying vec3 vSurfW;';

const SURF_VERT_BODY = [
  '{',
  '  vec4 surfWp = vec4( transformed, 1.0 );',
  '  #ifdef USE_BATCHING',
  '    surfWp = batchingMatrix * surfWp;',
  '  #endif',
  '  #ifdef USE_INSTANCING',
  '    surfWp = instanceMatrix * surfWp;',
  '  #endif',
  '  vSurfW = ( modelMatrix * surfWp ).xyz;',
  '}'
].join('\n');

const SURF_FRAG_DECL = [
  'varying vec3 vSurfW;',
  'uniform sampler2D uSurfTex;',
  'uniform vec4 uSurfA;',
  'uniform vec4 uSurfB;',
  'uniform float uSurfKind;'
].join('\n');

const SURF_FRAG_BODY = [
  '{',
  '  vec2 surfUv = vec2( vSurfW.x + vSurfW.z, vSurfW.y * 1.15 + vSurfW.z * 0.6 );',
  '  float surfMacro = texture2D( uSurfTex, surfUv * uSurfA.y ).b - 0.5;',
  '  diffuseColor.rgb *= 1.0 + surfMacro * uSurfA.x;',
  '  roughnessFactor = clamp( roughnessFactor + surfMacro * 0.16, 0.035, 1.0 );',
  '  roughnessFactor = max( roughnessFactor, mix( uSurfB.z, uSurfB.w, smoothstep( 4.0, 28.0, length( vViewPosition ) ) ) );',
  '  if ( uSurfKind > 0.5 ) {',
  '    vec2 surfDye = texture2D( uSurfTex, surfUv * 0.14 ).rg;',
  '    vec3 dyeA = vec3( 0.42, 0.49, 0.35 );',
  '    vec3 dyeB = vec3( 0.48, 0.34, 0.30 );',
  '    vec3 dyeC = vec3( 0.34, 0.40, 0.50 );',
  '    vec3 dye = mix( dyeA, dyeB, smoothstep( 0.32, 0.68, surfDye.x ) );',
  '    dye = mix( dye, dyeC, smoothstep( 0.58, 0.84, surfDye.y ) );',
  '    float clothLum = dot( diffuseColor.rgb, vec3( 0.299, 0.587, 0.114 ) );',
  '    diffuseColor.rgb = mix( diffuseColor.rgb, dye * ( 0.72 + clothLum * 0.72 ), 0.72 );',
  '  }',
  '  vec3 surfDx = dFdx( vSurfW );',
  '  vec3 surfDy = dFdy( vSurfW );',
  '  vec3 surfN = normalize( cross( surfDx, surfDy ) );',
  '  float surfWall = 1.0 - smoothstep( 0.30, 0.72, abs( surfN.y ) );',
  '  float surfBase = surfWall * uSurfB.x * smoothstep( uSurfB.y, 0.0, vSurfW.y );',
  '  surfBase = clamp( surfBase * ( 0.55 + ( surfMacro + 0.5 ) * 0.9 ), 0.0, 1.0 );',
  '  diffuseColor.rgb *= 1.0 - surfBase * vec3( 0.40, 0.42, 0.46 );',
  '  roughnessFactor = clamp( roughnessFactor + surfBase * 0.12, 0.035, 1.0 );',
  '  vec2 surfDet = texture2D( uSurfTex, surfUv * uSurfA.w ).rg - 0.5;',
  '  float surfFade = 1.0 - smoothstep( 1.4, 5.5, length( vViewPosition ) );',
  '  vec3 surfUpRef = abs( surfN.y ) < 0.9 ? vec3( 0.0, 1.0, 0.0 ) : vec3( 0.0, 0.0, 1.0 );',
  '  vec3 surfT = normalize( cross( surfUpRef, surfN ) );',
  '  vec3 surfB2 = cross( surfN, surfT );',
  '  vec3 surfOff = ( surfT * surfDet.x + surfB2 * surfDet.y ) * ( uSurfA.z * surfFade );',
  '  normal = normalize( normal + mat3( viewMatrix ) * surfOff );',
  '}'
].join('\n');

/**
 * Instala a camada de shader no material. Todos os materiais compartilham o
 * MESMO texto de GLSL (a variacao vive nos uniforms), entao o three reaproveita
 * o programa compilado - a chave de cache dele e o fonte do onBeforeCompile.
 * Se algum dos pontos de injecao nao existir, nao injeta nada: o material
 * continua funcionando exatamente como antes.
 */
function installSurfaceShader(mat, tex, cfg) {
  mat.onBeforeCompile = function (shader) {
    shader.uniforms.uSurfTex = { value: tex };
    shader.uniforms.uSurfA = { value: new THREE.Vector4(cfg[0], 1 / cfg[1], cfg[2], 1 / cfg[3]) };
    shader.uniforms.uSurfB = { value: new THREE.Vector4(cfg[4], cfg[5], cfg[6] || 0, cfg[7] || cfg[6] || 0) };
    shader.uniforms.uSurfKind = { value: cfg[8] || 0 };
    const vs = shader.vertexShader;
    const fs = shader.fragmentShader;
    if (vs.indexOf('#include <common>') < 0 || vs.indexOf('#include <project_vertex>') < 0) return;
    if (fs.indexOf('#include <common>') < 0 || fs.indexOf('#include <normal_fragment_maps>') < 0) return;
    shader.vertexShader = vs
      .replace('#include <common>', '#include <common>\n' + SURF_VERT_DECL)
      .replace('#include <project_vertex>', '#include <project_vertex>\n' + SURF_VERT_BODY);
    shader.fragmentShader = fs
      .replace('#include <common>', '#include <common>\n' + SURF_FRAG_DECL)
      .replace('#include <normal_fragment_maps>', '#include <normal_fragment_maps>\n' + SURF_FRAG_BODY);
  };
}

// ---------------------------------------------------------------------------
// biblioteca
// ---------------------------------------------------------------------------

export class MaterialLibrary {
  constructor(ctx) {
    this.ctx = ctx;
    this.size = TEX_SIZE;

    /** name -> material base (cache exigido pelo contrato). */
    this.cache = new Map();
    /** chave derivada (tile/variacao) -> material clonado. */
    this._derived = new Map();
    /** material -> superficie, para o surfaceOf nao depender de nome. */
    this._surfaceByMaterial = new WeakMap();

    this._textures = new Set();
    this._materials = new Set();
    this._fields = null;
    this._scratch = null;
    this._surfTex = null;
    this._aniso = 4;
    this._warned = false;
    this._unsubscribe = null;
    this._ready = false;
  }

  // -------------------------------------------------------------------------
  // ciclo de vida
  // -------------------------------------------------------------------------

  async init() {
    const t0 = (typeof performance !== 'undefined' ? performance.now() : 0);
    this._aniso = this._resolveAnisotropy();
    try {
      const S = this.size;
      const rng = mulberry32(0x5eed1a7e);
      this._surfTex = this._buildSurfaceTexture();
      this._fields = this._buildFields(S, rng);
      this._scratch = {
        col: new Float32Array(S * S * 3),
        hgt: new Float32Array(S * S),
        rgh: new Float32Array(S * S),
        mtl: new Float32Array(S * S),
        tmpA: new Float32Array(S * S),
        tmpB: new Float32Array(S * S)
      };
      this._rng = rng;

      // A ordem so importa para o tempo de carregamento: os pesados primeiro,
      // com um respiro para o browser nao marcar a aba como travada.
      this._defConcrete();
      this._defBrick();
      this._defAsphalt(false);
      this._defAsphaltLine();
      await this._breathe();
      // TRES rebocos de verdade, nao um com tinta diferente: cor de tinta,
      // densidade de casca e semente de mancha proprias. E o que da identidade
      // por predio - deslocar UV e tingir +-6% nao disfarca uma feicao grande.
      this._defPlaster('plaster', PLASTER_KINDS[0]);
      this._defTile();
      this._defMetal();
      this._defMetalThin();
      await this._breathe();
      this._defPlaster('plaster_b', PLASTER_KINDS[1]);
      this._defPlaster('plaster_c', PLASTER_KINDS[2]);
      await this._breathe();
      this._defWood();
      this._defGlass();
      this._defDirt();
      this._defSand();
      await this._breathe();
      this._defGravel();
      this._defGrass();
      this._defFabric();
      this._defRubber();
      await this._breathe();
      this._defSandbag();
      this._defWater();

      this._ready = true;
    } catch (err) {
      console.warn('[Materials] geracao procedural falhou, caindo para cores chapadas:', err);
      this._buildFallback();
    } finally {
      // ~20 MB de campos intermediarios: nao servem mais para nada em runtime
      this._fields = null;
      this._scratch = null;
    }

    // qualidade muda anisotropia; textura ja existe, so o filtro muda
    try {
      this._unsubscribe = this.ctx?.settings?.onChange?.((key) => {
        if (key === 'quality' || key === 'anisotropy') this._applyAnisotropy();
      }) || null;
    } catch {
      this._unsubscribe = null;
    }

    const ms = (typeof performance !== 'undefined' ? performance.now() : 0) - t0;
    if (ms > 1400) console.warn(`[Materials] geracao levou ${ms | 0} ms (alvo: menos de 1000 ms)`);
  }

  dispose() {
    if (this._unsubscribe) {
      try { this._unsubscribe(); } catch { /* ignorado */ }
      this._unsubscribe = null;
    }
    for (const tex of this._textures) {
      try { tex.dispose(); } catch { /* ignorado */ }
    }
    for (const mat of this._materials) {
      try { mat.dispose(); } catch { /* ignorado */ }
    }
    this._textures.clear();
    this._materials.clear();
    this.cache.clear();
    this._derived.clear();
    this._fields = null;
    this._scratch = null;
  }

  // -------------------------------------------------------------------------
  // API publica
  // -------------------------------------------------------------------------

  /** Material base cacheado. Nome desconhecido devolve concreto (com aviso unico). */
  get(name) {
    const mat = this.cache.get(name);
    if (mat) return mat;
    if (!this._warned) {
      this._warned = true;
      console.warn(`[Materials] material "${name}" nao existe; usando concreto`);
    }
    return this.cache.get('concrete') || this._makeFlat('concrete', 0x8d8d8a);
  }

  /**
   * Variante com repeticao propria. Clona o material E as texturas - o cache
   * base nunca e mutado. Cada combinacao (nome, rx, ry) e cacheada, para o Level
   * poder pedir a mesma parede mil vezes sem estourar memoria nem draw calls.
   */
  getTiled(name, repeatX = 1, repeatY = 1) {
    const base = this.get(name);
    const rx = Number.isFinite(+repeatX) && +repeatX > 0 ? +repeatX : 1;
    const ry = Number.isFinite(+repeatY) && +repeatY > 0 ? +repeatY : 1;
    if (Math.abs(rx - 1) < 1e-4 && Math.abs(ry - 1) < 1e-4) return base;

    const key = `${name}|t|${rx.toFixed(4)}|${ry.toFixed(4)}`;
    const hit = this._derived.get(key);
    if (hit) return hit;

    const mat = this._cloneWith(base, name, rx, ry, 0, 0);
    this._derived.set(key, mat);
    return mat;
  }

  /**
   * Variacao barata para o Level quebrar a repeticao: mesma textura, deslocada,
   * com tom e brilho levemente diferentes. NAO regenera textura (seria um engasgo
   * de dezenas de ms); o efeito de "outra parede" vem do deslocamento + tinta.
   * seed aceita numero ou string.
   */
  getVariant(name, seed = 0, repeatX = 1, repeatY = 1) {
    const h = typeof seed === 'number' ? (seed | 0) : hashString(seed);
    let sourceName = name;
    if (name === 'plaster') {
      const plasterPick = (hash2i(h, 7, 911) * 3) | 0;
      sourceName = plasterPick === 1 ? 'plaster_b' : (plasterPick === 2 ? 'plaster_c' : 'plaster');
    }
    const base = this.get(sourceName);
    const rx = Number.isFinite(+repeatX) && +repeatX > 0 ? +repeatX : 1;
    const ry = Number.isFinite(+repeatY) && +repeatY > 0 ? +repeatY : 1;

    const key = `${name}|v|${h}|${rx.toFixed(4)}|${ry.toFixed(4)}`;
    const hit = this._derived.get(key);
    if (hit) return hit;

    const ox = hash2i(h, 11, 3);
    const oy = hash2i(h, 22, 3);
    const mat = this._cloneWith(base, sourceName, rx, ry, ox, oy);

    const wall = name === 'plaster' || name === 'concrete';
    if (wall) {
      const palette = [
        [0.78, 0.82, 0.88],
        [1.10, 0.96, 0.76],
        [0.88, 0.72, 0.62],
        [1.12, 1.08, 0.96],
        [0.76, 0.86, 0.72]
      ];
      const tint = palette[(hash2i(h, 33, 3) * palette.length) | 0];
      const val = 0.82 + hash2i(h, 44, 3) * 0.30;
      mat.color.setRGB(clamp01(tint[0] * val), clamp01(tint[1] * val), clamp01(tint[2] * val));
    } else if (name === 'brick') {
      const val = 0.86 + hash2i(h, 44, 3) * 0.22;
      const warm = (hash2i(h, 33, 3) - 0.5) * 0.12;
      mat.color.setRGB(clamp01(val * (1 + warm)), clamp01(val), clamp01(val * (1 - warm * 0.65)));
    } else {
      const warm = (hash2i(h, 33, 3) - 0.5) * 0.16;
      const val = 0.82 + hash2i(h, 44, 3) * 0.30;
      mat.color.setRGB(clamp01(val * (1 + warm)), clamp01(val), clamp01(val * (1 - warm * 0.85)));
    }
    mat.roughness = clamp01(base.roughness * (0.88 + hash2i(h, 55, 3) * 0.20));

    this._derived.set(key, mat);
    return mat;
  }

  /**
   * Superficie de um objeto ou material, sempre um valor de SURFACES.
   * FX e Audio chamam isto por impacto de bala: nao aloca e nao lanca.
   */
  surfaceOf(target) {
    if (!target) return 'concrete';

    const ud = target.userData;
    if (ud) {
      const s = ud.surface;
      if (typeof s === 'string' && SURFACE_SET.has(s)) return s;
    }

    const mat = target.material;
    if (mat) {
      if (Array.isArray(mat)) {
        for (let i = 0; i < mat.length; i++) {
          const s = this._surfaceOfMaterial(mat[i]);
          if (s) return s;
        }
      } else {
        const s = this._surfaceOfMaterial(mat);
        if (s) return s;
      }
    }

    // o proprio alvo pode ser um material
    const own = this._surfaceOfMaterial(target);
    if (own) return own;

    // hitbox filha herda do pai (bot, prop composto)
    let p = target.parent;
    let guard = 0;
    while (p && guard++ < 4) {
      const pud = p.userData;
      if (pud) {
        const s = pud.surface;
        if (typeof s === 'string' && SURFACE_SET.has(s)) return s;
      }
      p = p.parent;
    }
    return 'concrete';
  }

  /** Metros de mundo cobertos por uma repeticao - use para calcular o repeat. */
  metersPerTile(name) {
    return METERS_PER_TILE[name] || 1.0;
  }

  /** Nomes disponiveis (o Level pode sortear entre eles). */
  names() {
    return Array.from(this.cache.keys());
  }

  // -------------------------------------------------------------------------
  // internos: clonagem e superficie
  // -------------------------------------------------------------------------

  _surfaceOfMaterial(mat) {
    if (!mat || typeof mat !== 'object') return null;
    const known = this._surfaceByMaterial.get(mat);
    if (known) return known;
    const ud = mat.userData;
    if (ud && typeof ud.surface === 'string' && SURFACE_SET.has(ud.surface)) return ud.surface;
    if (typeof mat.name === 'string' && SURFACE_SET.has(mat.name)) return mat.name;
    return null;
  }

  _cloneWith(base, name, rx, ry, ox, oy) {
    const mat = base.clone();
    mat.name = base.name;
    // Material.copy NAO copia onBeforeCompile: sem esta linha o clone perderia
    // a camada de shader, e como o Level usa clone em quase tudo, o mapa
    // inteiro ficaria sem macro, sem detalhe e sem encardido de base.
    mat.onBeforeCompile = base.onBeforeCompile;

    // roughnessMap/aoMap/metalnessMap sao A MESMA textura (ORM): um clone so
    const seen = new Map();
    for (let i = 0; i < MAP_SLOTS.length; i++) {
      const slot = MAP_SLOTS[i];
      const src = base[slot];
      if (!src) continue;
      let clone = seen.get(src);
      if (!clone) {
        clone = src.clone();
        clone.wrapS = THREE.RepeatWrapping;
        clone.wrapT = THREE.RepeatWrapping;
        clone.repeat.set(rx, ry);
        clone.offset.set(ox, oy);
        clone.anisotropy = src.anisotropy;
        clone.colorSpace = src.colorSpace;
        clone.needsUpdate = true;
        seen.set(src, clone);
        this._textures.add(clone);
      }
      mat[slot] = clone;
    }

    const surface = this._surfaceOfMaterial(base) || 'concrete';
    mat.userData = mat.userData || {};
    mat.userData.surface = surface;
    this._surfaceByMaterial.set(mat, surface);
    this._materials.add(mat);
    return mat;
  }

  _resolveAnisotropy() {
    const want = this.ctx?.settings?.anisotropy ?? 4;
    let max = 8;
    try {
      max = this.ctx?.renderer?.capabilities?.getMaxAnisotropy?.() ?? 8;
    } catch {
      max = 8;
    }
    return Math.max(1, Math.min(want, max));
  }

  _applyAnisotropy() {
    const a = this._resolveAnisotropy();
    if (a === this._aniso) return;
    this._aniso = a;
    for (const tex of this._textures) {
      tex.anisotropy = a;
      tex.needsUpdate = true;
    }
  }

  _breathe() {
    // devolve o controle ao browser entre blocos pesados de geracao
    return new Promise((resolve) => setTimeout(resolve, 0));
  }

  // -------------------------------------------------------------------------
  // internos: campos compartilhados
  // -------------------------------------------------------------------------

  /**
   * Textura UNICA (256x256) que serve a camada de shader do mapa inteiro:
   *   RG = inclinacao de um ruido de alta frequencia (detalhe de perto)
   *   B  = campo macro de baixa frequencia (mancha em coordenada de mundo)
   * Custa ~12 ms e vale por um mapa de detalhe em cada material.
   */
  _buildSurfaceTexture() {
    const D = 256, M = D - 1;
    const rng = mulberry32(0x51d3771);
    const fine = fbm(D, 40, 40, 3, 0.55, rng);
    const micro = fbm(D, 88, 88, 2, 0.50, rng);
    const macro = fbm(D, 3, 3, 4, 0.58, rng);
    const h = new Float32Array(D * D);
    for (let i = 0; i < h.length; i++) h[i] = fine[i] * 0.62 + micro[i] * 0.38;

    const data = new Uint8ClampedArray(D * D * 4);
    for (let y = 0; y < D; y++) {
      const row = y * D;
      const up = ((y - 1) & M) * D;
      const dn = ((y + 1) & M) * D;
      for (let x = 0; x < D; x++) {
        const xl = (x - 1) & M, xr = (x + 1) & M;
        const gx = (h[row + xr] - h[row + xl]) * 0.5;
        const gy = (h[dn + x] - h[up + x]) * 0.5;
        const j = (row + x) * 4;
        data[j] = clamp01(0.5 - gx * 3.4) * 255;
        data[j + 1] = clamp01(0.5 + gy * 3.4) * 255;
        // o fbm cru fica espremido em torno de 0.5; a rampa devolve contraste
        data[j + 2] = smoothstep(0.24, 0.76, macro[row + x]) * 255;
        data[j + 3] = 255;
      }
    }

    const canvas = newCanvas(D);
    canvas.getContext('2d').putImageData(new ImageData(data, D, D), 0, 0);
    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = this._aniso;
    tex.needsUpdate = true;
    this._textures.add(tex);
    return tex;
  }

  _buildFields(S, rng) {
    const low = fbm(S, 3, 3, 4, 0.55, rng);
    const mid = fbm(S, 11, 11, 4, 0.50, rng);
    const fine = fbm(S, 37, 37, 3, 0.50, rng);
    const micro = fbm(S, 97, 97, 2, 0.50, rng);
    const warp = fbm(S, 5, 5, 3, 0.52, rng);
    // esticado no eixo Y: e o que faz mancha escorrida de chuva parecer chuva
    const streak = fbm(S, 29, 3, 4, 0.55, rng);
    // Campo de VINCO (lona amassada), nao de trinca: ridged e uma cordilheira
    // suave, boa para amassado e pessima para rachadura (ver drawCrackField).
    const crack = ridged(fbm(S, 7, 7, 3, 0.52, rng));
    // Rede de trinca de verdade: ramifica em Y e afina na ponta. Semente propria
    // para nao consumir o rng dos campos - mexer aqui nao remexe o resto da
    // biblioteca (tijolo, metal e madeira continuam identicos).
    // 12 sementes viram ~27 ramos, ~6 m de trinca por tile de 1,60 m; com o
    // gate de baixa frequencia dos materiais sobra menos de 1 m/m2 visivel, que
    // e uma ou duas trincas por painel - nao uma malha cobrindo tudo.
    const crackNet = channelField(
      drawCrackField(S, 12, mulberry32(0x2c7a19), {
        min: 40, max: 175, width: 1.55, wander: 0.44, branch: 0.80
      }), S, 0);
    const white = new Float32Array(S * S);
    for (let i = 0; i < white.length; i++) white[i] = rng();
    return {
      low, mid, fine, micro, warp, streak, crack, crackNet, white,
      vBig: voronoi(S, 26, rng),
      vMid: voronoi(S, 64, rng),
      vFine: voronoi(S, 150, rng)
    };
  }

  // -------------------------------------------------------------------------
  // internos: finalizacao (AO, normal, texturas, material)
  // -------------------------------------------------------------------------

  /**
   * Converte os buffers de trabalho em texturas e registra o material.
   *
   * opts:
   *   height        profundidade REAL do relevo em metros (define a forca da normal)
   *   aoRadius      raio do blur de cavidade, em pixels
   *   aoStrength    0..1
   *   normalScale   ajuste fino por cima do calculo fisico
   *   metalnessMap  true quando a metalicidade varia no espaco (metal raspado)
   *   physical      usa MeshPhysicalMaterial
   *   params        parametros extras jogados no material
   */
  _finish(name, surface, opts) {
    const S = this.size;
    const N = S * S;
    const { col, hgt, rgh, mtl, tmpA, tmpB } = this._scratch;

    // --- AO de cavidade: quem esta abaixo da media local recebe sombra ------
    const blurred = boxBlurWrap(hgt, S, opts.aoRadius ?? 6, tmpA, tmpB);
    const aoK = opts.aoStrength ?? 0.8;

    const albData = new Uint8ClampedArray(N * 4);
    const ormData = new Uint8ClampedArray(N * 4);
    for (let i = 0; i < N; i++) {
      const occ = clamp01((blurred[i] - hgt[i]) * 2.6) * aoK;
      const j = i * 4;
      const c = i * 3;
      // parte da AO tambem entra no albedo: fecha o contato e da profundidade
      // mesmo quando o SSAO estiver desligado na qualidade baixa
      const shade = 1 - occ * 0.32;
      albData[j] = col[c] * shade * 255;
      albData[j + 1] = col[c + 1] * shade * 255;
      albData[j + 2] = col[c + 2] * shade * 255;
      albData[j + 3] = 255;
      ormData[j] = (1 - occ) * 255;
      ormData[j + 1] = rgh[i] * 255;
      ormData[j + 2] = mtl[i] * 255;
      ormData[j + 3] = 255;
    }

    // --- normal a partir da altura -----------------------------------------
    const mpt = METERS_PER_TILE[name] || 1.0;
    const metersPerPixel = mpt / S;
    const slope = ((opts.height ?? 0.005) / metersPerPixel) * (opts.normalScale ?? 1);
    const nrmData = this._normalFromHeight(hgt, S, slope);

    // --- texturas -----------------------------------------------------------
    const albedo = this._texture(albData, true);
    const normal = this._texture(nrmData, false);
    const orm = this._texture(ormData, false);

    // --- material -----------------------------------------------------------
    const params = {
      name,
      map: albedo,
      normalMap: normal,
      normalScale: new THREE.Vector2(1, 1),
      roughnessMap: orm,
      roughness: 1,
      metalness: opts.metalnessMap ? 1 : (opts.metalness ?? 0),
      dithering: true,
      side: opts.side || THREE.FrontSide
    };
    if (opts.metalnessMap) params.metalnessMap = orm;
    if (opts.aoMap !== false) params.aoMap = orm;
    if (opts.params) Object.assign(params, opts.params);

    const mat = opts.physical
      ? new THREE.MeshPhysicalMaterial(params)
      : new THREE.MeshStandardMaterial(params);

    mat.userData.surface = surface;
    const cfg = SURF_LAYER[name];
    if (cfg && this._surfTex) installSurfaceShader(mat, this._surfTex, cfg);
    this._register(name, surface, mat);
    return mat;
  }

  _normalFromHeight(h, S, slope) {
    const M = S - 1;
    const out = new Uint8ClampedArray(S * S * 4);
    const LIMIT = 6; // trava a inclinacao: relevo exagerado vira plastico bolhudo
    for (let y = 0; y < S; y++) {
      const row = y * S;
      const up = ((y - 1) & M) * S;
      const dn = ((y + 1) & M) * S;
      for (let x = 0; x < S; x++) {
        const xl = (x - 1) & M;
        const xr = (x + 1) & M;
        // sobel: gradiente bem mais estavel que diferenca central pura
        const gx = ((h[up + xr] + 2 * h[row + xr] + h[dn + xr]) -
                    (h[up + xl] + 2 * h[row + xl] + h[dn + xl])) * 0.125;
        const gy = ((h[dn + xl] + 2 * h[dn + x] + h[dn + xr]) -
                    (h[up + xl] + 2 * h[up + x] + h[up + xr])) * 0.125;
        let nx = -gx * slope;
        let ny = gy * slope;   // +: v decresce com y (flipY) - ver cabecalho
        if (nx > LIMIT) nx = LIMIT; else if (nx < -LIMIT) nx = -LIMIT;
        if (ny > LIMIT) ny = LIMIT; else if (ny < -LIMIT) ny = -LIMIT;
        const inv = 1 / Math.sqrt(nx * nx + ny * ny + 1);
        const j = (row + x) * 4;
        out[j] = (nx * inv * 0.5 + 0.5) * 255;
        out[j + 1] = (ny * inv * 0.5 + 0.5) * 255;
        out[j + 2] = (inv * 0.5 + 0.5) * 255;
        out[j + 3] = 255;
      }
    }
    return out;
  }

  _texture(data, srgb) {
    const S = this.size;
    const canvas = newCanvas(S);
    const g = canvas.getContext('2d');
    g.putImageData(new ImageData(data, S, S), 0, 0);
    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = this._aniso;
    if (srgb) tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    this._textures.add(tex);
    return tex;
  }

  _register(name, surface, mat) {
    this.cache.set(name, mat);
    this._materials.add(mat);
    this._surfaceByMaterial.set(mat, surface);
  }

  _makeFlat(name, hex) {
    const mat = new THREE.MeshStandardMaterial({
      name, color: hex, roughness: 0.88, metalness: 0, dithering: true
    });
    const surface = SURFACE_SET.has(name) ? name : 'concrete';
    mat.userData.surface = surface;
    this._register(name, surface, mat);
    return mat;
  }

  _buildFallback() {
    for (const key of Object.keys(METERS_PER_TILE)) {
      if (this.cache.has(key)) continue;
      const surface = key === 'asphalt_line' ? 'asphalt' : key;
      const mat = this._makeFlat(key, FALLBACK_COLOR[key] ?? 0x8a8a8a);
      mat.userData.surface = SURFACE_SET.has(surface) ? surface : 'concrete';
      this._surfaceByMaterial.set(mat, mat.userData.surface);
    }
  }

  // =========================================================================
  // MATERIAIS
  // =========================================================================

  /** Concreto: poro, agregado a mostra, trinca fina, mancha de umidade. */
  _defConcrete() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;

    // Bolhas de ar alongadas deixadas junto da forma. Sao poucas, de largura e
    // profundidade diferentes, e quebram a leitura de todos os vazios redondos.
    const airVoids = channelField(
      drawStrokes(S, 28, mulberry32(0xc04c37), {
        min: 5, max: 19, width: 2.5, bend: 5,
        alpha: (r) => 0.28 + r() * 0.58
      }), S, 0);

    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 137) & M) * S;
      const rowB = ((y + 401) & M) * S;
      const rowC = ((y + 293) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + 53) & M);
        const ib = rowB + ((x + 229) & M);
        const ic = rowC + ((x + 167) & M);
        const nl = F.low[ia];
        const nm = F.mid[i];
        const nf = F.fine[i];
        const nu = F.micro[i];
        const streak = F.streak[ib];

        // encardido e desbotado como MANCHA (ver nota 6 do cabecalho: rampa
        // vertical dentro do tile nao fecha na emenda)
        const dirt = smoothstep(0.44, 0.78, F.low[i]);
        const bleach = smoothstep(0.52, 0.86, nm);

        // Poros nao podem nascer em todas as celulas de uma unica rede: mesmo
        // com centros jitterados isso vira reticula. Duas populacoes muito
        // esparsas usam fases diferentes, raio com cauda longa e contorno
        // deformado pelo ruido. O gate concentra os poros em manchas.
        const poreCluster = smoothstep(0.43, 0.72, nm * 0.62 + F.warp[ib] * 0.38);

        const fineIdA = F.vFine.id[i];
        const fineHashA = hash2i((fineIdA * 8192) | 0, 5, 97);
        const fineSizeA = 0.30 + Math.pow(fineHashA, 3) * 1.45;
        const fineShapeA = F.vFine.d1[i] * (1 + (nf - 0.5) * 0.72 + (nu - 0.5) * 0.26);
        const poreA = (fineIdA > 0.986 - poreCluster * 0.020 ? 1 : 0)
                    * smoothstep(0.50 * fineSizeA, 0.13 * fineSizeA, fineShapeA);

        const fineIdB = F.vFine.id[ia];
        const fineHashB = hash2i((fineIdB * 8192) | 0, 19, 211);
        const fineSizeB = 0.42 + Math.pow(fineHashB, 2) * 1.10;
        const fineShapeB = F.vFine.d1[ia] * (1 + (F.fine[ia] - 0.5) * 0.80);
        const poreB = (fineIdB > 0.995 ? 1 : 0)
                    * smoothstep(0.46 * fineSizeB, 0.12 * fineSizeB, fineShapeB);

        // Bolhas maiores sao raras e vem de outra escala; nao sao apenas o
        // mesmo ponto pequeno ampliado. O centro tambem varia em profundidade.
        const largeId = F.vMid.id[ib];
        const largeHash = hash2i((largeId * 8192) | 0, 31, 307);
        const largeSize = 0.28 + Math.pow(largeHash, 2) * 0.72;
        const largeShape = F.vMid.d1[ib] * (1 + (F.mid[ib] - 0.5) * 0.62 + (nf - 0.5) * 0.24);
        const poreLarge = (largeId > 0.992 - poreCluster * 0.006 ? 1 : 0)
                        * smoothstep(0.48 * largeSize, 0.11 * largeSize, largeShape);
        const poreLong = smoothstep(0.10, 0.74, airVoids[i])
                       * (0.46 + hash2i(x >> 3, y >> 3, 419) * 0.44);

        const depthA = 0.48 + hash2i((fineIdA * 16384) | 0, 7, 151) * 0.40;
        const depthB = 0.42 + hash2i((fineIdB * 16384) | 0, 13, 233) * 0.42;
        const depthLarge = 0.66 + largeHash * 0.30;
        const pore = Math.max(poreA * depthA, poreB * depthB,
                              poreLarge * depthLarge, poreLong);

        // Agregado so aparece onde a nata superficial gastou. Fragmentos de
        // outra escala substituem o pontilhado claro uniforme que havia antes.
        const aggId = F.vMid.id[ic];
        const aggSize = 0.32 + hash2i((aggId * 8192) | 0, 23, 173) * 0.58;
        const aggWear = smoothstep(0.48, 0.79, bleach * 0.58 + F.low[ib] * 0.42);
        const agg = (aggId > 0.93 ? 1 : 0)
                  * smoothstep(0.50 * aggSize, 0.14 * aggSize,
                               F.vMid.d1[ic] * (1 + (nf - 0.5) * 0.55))
                  * aggWear;

        // Trinca vem da rede DESENHADA (ramifica, afina, tem ponta). O que havia
        // aqui era smoothstep sobre ruido ridged: isso e a isolinha do ruido de
        // base, ou seja curva de nivel - laco preto fechado por toda a laje.
        // O gate de baixa frequencia deixa a MAIOR PARTE do tile sem trinca.
        const crack = F.crackNet[ic] * smoothstep(0.45, 0.78, F.low[i]);
        // umidade: mancha larga + escorrido vertical (o campo streak e esticado
        // em Y, entao a marca de chuva desce a parede e ainda fecha no tile)
        const damp = clamp01(smoothstep(0.58, 0.88, nl) * 0.75 + smoothstep(0.56, 0.92, streak) * 0.60);

        // Base em sRGB. 0.585 dava ~0.30-0.45 LINEAR: concreto envelhecido vive
        // em 0.18-0.28, e como concreto cobre meia tela a cidade inteira saia
        // branca. O contraste agora vem da sujeira, que morde de verdade.
        let base = 0.505 + (nl - 0.5) * 0.055 + (nm - 0.5) * 0.065 + (nf - 0.5) * 0.03;
        base += bleach * 0.035;
        let r = base, g = base * 1.006, b = base * 0.998;
        const aggTone = 0.035 + aggId * 0.045;
        r += agg * aggTone;
        g += agg * aggTone * 0.98;
        b += agg * aggTone * 0.91;
        const poreDark = 0.105 + F.white[ia] * 0.105;
        r -= pore * poreDark;
        g -= pore * poreDark * 0.98;
        b -= pore * poreDark * 0.94;
        r -= damp * 0.065; g -= damp * 0.060; b -= damp * 0.055;
        // a trinca escurece por SOMBRA (AO + sulco no normal), nao por pigmento
        const cd = crack * 0.06;
        r -= cd; g -= cd; b -= cd;
        const dg = dirt * (0.55 + nf * 0.9);
        r -= dg * 0.040; g -= dg * 0.045; b -= dg * 0.050;

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        hgt[i] = 0.55 + (nf - 0.5) * 0.20 + (nu - 0.5) * 0.14 + (nm - 0.5) * 0.10
               + agg * 0.07 - pore * 0.17 - crack * 0.22;
        rgh[i] = 0.90 + (nu - 0.5) * 0.10 - damp * 0.30 + pore * 0.06 - agg * 0.05
               + dirt * 0.03 + crack * 0.04;
        mtl[i] = 0;
      }
    }
    this._finish('concrete', 'concrete', {
      height: 0.019, aoRadius: 6, aoStrength: 0.80, normalScale: 1.0
    });
  }

  /** Tijolo aparente em amarracao corrida, com argamassa irregular. */
  _defBrick() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    const out = BRICK_OUT;
    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowB = ((y + 311) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        sampleBrick(x, y, i, S, F, 6, 12, 3.1, out);
        const streak = F.streak[rowB + ((x + 89) & M)];
        // mancha e eflorescencia por ruido (ver nota 6 do cabecalho)
        const dirt = smoothstep(0.46, 0.80, F.low[i]);
        const salt = smoothstep(0.58, 0.86, F.warp[i]);

        let r = out.r, g = out.g, b = out.b;
        // escorrido de chuva sobre a fiada
        const wash = smoothstep(0.56, 0.92, streak) * 0.75;
        r -= wash * 0.055; g -= wash * 0.050; b -= wash * 0.040;
        // sal branco na argamassa perto do chao
        const eff = salt * (1 - out.face) * smoothstep(0.4, 0.8, F.mid[i]);
        r += eff * 0.16; g += eff * 0.16; b += eff * 0.15;
        // sujeira de rua na base
        const dg = dirt * (0.5 + F.fine[i]);
        r -= dg * 0.050; g -= dg * 0.057; b -= dg * 0.064;

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        hgt[i] = out.h;
        // A mancha de escorrido TEM que aparecer no roughness, senao le como
        // tinta chapada. Tijolo lavado pela chuva fica polido; a faixa que nunca
        // molha continua fosca. Isso e o que da modelado a luz rasante.
        rgh[i] = out.ro - wash * 0.26 + eff * 0.04 - smoothstep(0.60, 0.95, F.low[i]) * 0.10;
        mtl[i] = 0;
      }
    }
    this._finish('brick', 'brick', {
      height: 0.013, aoRadius: 5, aoStrength: 0.92, normalScale: 1.0
    });
  }

  /**
   * Asfalto: brita fina exposta, remendo mais escuro, mancha de oleo (que
   * escurece E alisa - e o detalhe que mais entrega rua de verdade).
   * withLine liga a faixa de tinta desgastada no centro do tile (eixo U).
   */
  _defAsphalt(withLine) {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 61) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + 311) & M);
        const nl = F.low[i], nm = F.mid[ia], nf = F.fine[i], nu = F.micro[i];

        // Agregado na escala CERTA. Vinha do voronoi de 150 celulas (~1,5 cm por
        // celula) com mascara que so saturava a meio pixel do centro: some no
        // primeiro mip e sobra vinil preto. Agora a brita vem do voronoi de 64
        // (~3,4 cm, sobrevive a 10 m) e o de 150 fica so como micro-aspereza.
        const dAgg = F.vMid.d1[i];
        const aid = F.vMid.id[i];
        const grit = 1 - smoothstep(0.0, 0.55, F.vFine.d1[i]);
        const aggOn = aid > 0.66 ? 1 : 0;
        const aggSize = 0.58 + hash2i((aid * 8192) | 0, 17, 149) * 0.95;
        const aggWarp = 1 + (nf - 0.5) * 0.25;
        const stone = aggOn * smoothstep(0.62 * aggSize, 0.16 * aggSize, dAgg * aggWarp);
        const patch = smoothstep(0.56, 0.71, nl);
        const resurf = smoothstep(0.30, 0.70, F.warp[i]) - 0.5;   // recapeamento
        const oil = smoothstep(0.74, 0.94, F.low[ia]) * (0.45 + nm * 0.7);

        // 0.163 vinha com o comentario de "nao descer de 0.02 linear" - so que
        // este valor e gravado em sRGB, e 0.163 sRGB E 0.023 linear, exatamente
        // o piso que se queria evitar. Asfalto seco vive em 0.08-0.12 linear.
        let v = 0.345 + (nl - 0.5) * 0.075 + (nf - 0.5) * 0.040 + resurf * 0.075;
        v *= (1 - patch * 0.26);
        v = lerp(v, 0.270 + aid * 0.190, stone * 0.78);
        v += (nu - 0.5) * 0.030 + grit * 0.020;

        let r = v * 1.02, g = v * 1.0, b = v * 1.035;
        r -= oil * 0.055; g -= oil * 0.048; b -= oil * 0.030;

        let h = 0.46 + stone * 0.14 - smoothstep(0.18, 0.0, F.vMid.d2[i]) * stone * 0.08
              + grit * 0.07 + (nf - 0.5) * 0.10 + (nu - 0.5) * 0.10 - patch * 0.05;
        let ro = 0.870 + (nu - 0.5) * 0.10 - oil * 0.42 - patch * 0.05
               + stone * 0.02 - resurf * 0.06;

        if (withLine) {
          const u = x / S;
          // faixa de ~15 cm no meio do tile; a tinta se apaga onde a brita sobe
          const band = 1 - smoothstep(0.030, 0.042, Math.abs(u - 0.5));
          const wear = smoothstep(0.30, 0.70, nf) * smoothstep(0.26, 0.74, nm);
          // a brita agora vem do voronoi de 64 e cobre bem mais area: com o peso
          // antigo (0.45) a faixa de tinta sumia quase inteira
          const paint = clamp01(band * (wear * 1.6 - stone * 0.26 - smoothstep(0.55, 0.85, nl) * 0.7));
          r = lerp(r, 0.760, paint);
          g = lerp(g, 0.730, paint);
          b = lerp(b, 0.640, paint);
          h += paint * 0.10;
          ro = lerp(ro, 0.700 + (nu - 0.5) * 0.12, paint);
        }

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        hgt[i] = h;
        rgh[i] = ro;
        mtl[i] = 0;
      }
    }
    this._finish(withLine ? 'asphalt_line' : 'asphalt', 'asphalt', {
      height: 0.007, aoRadius: 4, aoStrength: 0.85, normalScale: 1.0
    });
  }

  _defAsphaltLine() {
    this._defAsphalt(true);
  }

  /**
   * Reboco pintado descascando, mostrando tijolo por baixo.
   *
   * `kind` traz cor da tinta, limiar da casca e deslocamento de fase: e o que
   * faz plaster, plaster_b e plaster_c serem PAREDES diferentes (tinta, quanto
   * caiu e onde caiu), nao a mesma parede com outra demao. getVariant sorteia
   * entre elas, entao dois predios vizinhos nao repetem nem o desenho da casca.
   */
  _defPlaster(name, kind) {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl, tmpA } = this._scratch;
    const out = BRICK_OUT;
    // Cor da tinta em sRGB. 0.760/0.720/0.640 vezes um base de ate 1.10 chegava
    // a ~0.66 LINEAR: fachada estourada. Tinta desbotada pelo sol vive em
    // 0.35-0.45 linear, que e onde estes valores caem.
    const PR = kind.r, PG = kind.g, PB = kind.b;
    const OX = kind.ox, OY = kind.oy;

    // --- passo 1: mascara da casca -----------------------------------------
    // ESCALA e RARIDADE sao o assunto aqui, nao a borda.
    // O recorte saia de F.low, que tem 3 celulas por tile: num tile de 1,80 m
    // isso e um continente de 60 a 90 cm, ou seja METADE DA PAREDE, em altissimo
    // contraste (tijolo escuro contra reboco claro). Feicao desse tamanho e
    // reconhecida na hora quando repete - era o "papel de parede de estampa de
    // onca". Agora quem recorta e F.mid (11 celulas = ~16 cm), que e o tamanho
    // real de uma placa de reboco que solta, e o campo de 3 celulas virou GATE
    // multiplicativo: decide so ONDE a parede esta ruim. Resultado: a maior
    // parte do tile nao tem casca nenhuma, e o que tem e do tamanho de uma mao.
    // A mascara vai para um buffer proprio porque o passo 2 precisa do GRADIENTE
    // dela - e o gradiente e o unico jeito de ter borda de 1-2 px.
    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + OY) & M) * S;
      const rowG = ((y + OY + 91) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + OX) & M);
        const ig = rowG + ((x + OX + 137) & M);
        sampleBrick(x, y, i, S, F, 9, 18, 2.4, out);
        const gate = smoothstep(0.62, 0.80, F.low[ia]);
        const pf = F.mid[ia] * 0.72
                 + F.fine[ig] * 0.23
                 + F.warp[i] * 0.12
                 + (F.micro[i] - 0.5) * 0.06
                 + (1 - out.face) * 0.025;
        // O campo macro apenas AUTORIZA a falha; ele nunca desenha a silhueta.
        // Assim a casca e rara e formada por placas de 10 a 25 cm.
        tmpA[i] = gate * smoothstep(kind.cut, kind.cut + 0.032, pf);
      }
    }

    // --- passo 2: cor, relevo e acabamento ---------------------------------
    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + OY) & M) * S;
      const rowB = ((y + 67) & M) * S;
      const rowU = ((y - 1) & M) * S;
      const rowD = ((y + 1) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + OX) & M);
        const ib = rowB + ((x + 19) & M);
        const nl = F.low[ia], nm = F.mid[i], nf = F.fine[i], nu = F.micro[i];
        const streak = F.streak[ib];
        // encardido e desbotado por ruido (ver nota 6 do cabecalho). Amplitude
        // baixa DE PROPOSITO: a mancha larga agora vem da camada de shader, em
        // coordenada de mundo (nota 7). Mancha grande assada no tile e o que
        // fazia a fachada inteira ler como grade da mesma mancha.
        const dirt = smoothstep(0.44, 0.78, F.low[i]);
        const sun = smoothstep(0.52, 0.86, nm);

        const peel = tmpA[i];
        // borda pelo GRADIENTE da mascara: 1-2 px sempre, qualquer que seja a
        // inclinacao do campo. A faixa de VALOR que havia aqui virava um halo
        // claro de dezenas de pixels em volta de cada mancha (medido: 18% da
        // textura) - o defeito que fazia o descascado parecer adesivo colado.
        const rim = clamp01((Math.abs(tmpA[row + ((x + 1) & M)] - tmpA[row + ((x - 1) & M)])
                           + Math.abs(tmpA[rowD + x] - tmpA[rowU + x])) * 1.8);

        sampleBrick(x, y, i, S, F, 9, 18, 2.4, out);

        // reboco: desempeno deixa ondulacao larga + poro fino
        let base = 0.98 + (nm - 0.5) * 0.07 + (nf - 0.5) * 0.05 + (F.warp[i] - 0.5) * 0.045;
        let r = PR * base, g = PG * base, b = PB * base;
        r += sun * 0.022; g += sun * 0.020; b += sun * 0.016;

        // mistura o tijolo exposto
        r = lerp(r, out.r * 0.92, peel);
        g = lerp(g, out.g * 0.92, peel);
        b = lerp(b, out.b * 0.92, peel);
        // a lasca deixa a massa cinza a mostra: a borda e mais ESCURA que a
        // tinta desbotada em volta, nunca mais clara.
        r -= rim * 0.055; g -= rim * 0.052; b -= rim * 0.048;

        const wash = smoothstep(0.54, 0.92, streak) * 0.8 * kind.wash;
        r -= wash * 0.032; g -= wash * 0.030; b -= wash * 0.026;
        const dg = dirt * (0.5 + nf * 0.9);
        r -= dg * 0.024; g -= dg * 0.026; b -= dg * 0.028;

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        const plasterH = 0.78 + (nm - 0.5) * 0.07 + (nu - 0.5) * 0.085
                       + (nf - 0.5) * 0.10 + (F.warp[i] - 0.5) * 0.06;
        // degrau de verdade: a espessura do reboco aparece por sombreamento. E o
        // tijolo do fundo mantem o relevo INTEIRO (estava comprimido a 55%, por
        // isso o miolo do buraco lia como borrado ao lado da borda nitida).
        hgt[i] = lerp(plasterH, out.h * 0.92 - 0.20, peel);
        // escorrido de chuva ALISA a superficie; o sinal estava invertido.
        rgh[i] = lerp(0.795 + (nu - 0.5) * 0.09 - wash * 0.24 - dirt * 0.06,
                      out.ro * 1.02, peel);
        mtl[i] = 0;
      }
    }
    this._finish(name, 'plaster', {
      height: 0.024, aoRadius: 6, aoStrength: 0.85, normalScale: 1.0
    });
  }

  /** Piso ceramico: 4x4 placas de 30 cm, rejunte sujo e recuado, esmalte riscado. */
  _defTile() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    const scratch = channelField(
      drawStrokes(S, 260, mulberry32(0x71133), {
        min: 8, max: 70, width: 0.85, bend: 10,
        alpha: (r) => 0.16 + r() * 0.5
      }), S, 0);

    const COLS = 4, ROWS = 4;
    const tw = S / COLS, th = S / ROWS;
    const groutPx = 3.0;
    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowC = ((y + 97) & M) * S;
      const trow = Math.floor(y / th);
      const vIn = y / th - trow;
      const dyp = Math.min(vIn, 1 - vIn) * th;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ic = rowC + ((x + 311) & M);
        const tcol = Math.floor(x / tw);
        const uIn = x / tw - tcol;
        const dxp = Math.min(uIn, 1 - uIn) * tw;
        const d = Math.min(dxp, dyp) - (F.fine[i] - 0.5) * 1.4;
        const face = smoothstep(groutPx, groutPx + 2.6, d);

        const tid = hash2i(tcol, trow, 17);
        const broken = hash2i(tcol, trow, 53) > 0.86;
        // veio de marmorizado dentro da placa
        const vein = smoothstep(0.52, 0.78, F.mid[i] * 0.7 + F.warp[i] * 0.5);
        // ceramica encardida vive em 0.20-0.30 linear: 0.735 sRGB dava 0.50 e o
        // galpao abandonado ficava com piso de azulejo novo estourado de luz
        let base = 0.560 + (tid - 0.5) * 0.075 + (F.low[i] - 0.5) * 0.05;
        let r = base * 1.005, g = base * 0.995, b = base * 0.955;
        r -= vein * 0.075; g -= vein * 0.070; b -= vein * 0.060;

        // trinca so na placa quebrada, vinda da rede desenhada: a cordilheira
        // ridged que estava aqui devolvia laco fechado de largura constante
        const crack = broken ? F.crackNet[ic] : 0;
        r -= crack * 0.10; g -= crack * 0.10; b -= crack * 0.095;

        // rejunte: cinza, poroso e encardido
        const gsum = 0.385 + (F.fine[i] - 0.5) * 0.12 - smoothstep(0.4, 0.9, F.mid[i]) * 0.08;
        r = lerp(gsum * 1.0, r, face);
        g = lerp(gsum * 0.985, g, face);
        b = lerp(gsum * 0.945, b, face);

        const sc = scratch[i] * face;
        const c = i * 3;
        col[c] = r + sc * 0.045;
        col[c + 1] = g + sc * 0.045;
        col[c + 2] = b + sc * 0.045;
        hgt[i] = lerp(0.28 + (F.fine[i] - 0.5) * 0.10, 0.80 + (F.micro[i] - 0.5) * 0.03 - crack * 0.26, face);
        // esmalte e liso; o rejunte e fosco. Risco no esmalte fosca de leve.
        rgh[i] = lerp(0.930, 0.300 + (F.micro[i] - 0.5) * 0.09 + sc * 0.30 + crack * 0.35, face);
        mtl[i] = 0;
      }
    }
    this._finish('tile', 'tile', {
      height: 0.013, aoRadius: 5, aoStrength: 0.90, normalScale: 1.0
    });
  }

  /** Chapa de aco pintada: risco expondo metal, ferrugem, amassado. */
  _defMetal() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    const rngS = mulberry32(0x9a11c3);
    const scratch = channelField(
      drawStrokes(S, 420, rngS, {
        min: 6, max: 90, width: 0.9, bend: 14,
        alpha: (r) => 0.2 + r() * 0.75
      }), S, 0);

    // verde industrial dessaturado (container/porta de galpao)
    const PR = 0.205, PG = 0.262, PB = 0.238;
    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 149) & M) * S;
      const rowB = ((y + 401) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + 97) & M);
        const ib = rowB + ((x + 251) & M);
        const nl = F.low[ia], nm = F.mid[i], nf = F.fine[i], nu = F.micro[i];
        const streak = F.streak[ib];
        // encardido por ruido (ver nota 6 do cabecalho)
        const bottom = smoothstep(0.46, 0.82, F.low[i]);

        // ferrugem: onde a tinta ja saiu, seguindo o escorrido
        const rustField = nl * 0.62 + smoothstep(0.5, 0.95, streak) * 0.30 + bottom * 0.20;
        const rust = smoothstep(0.505, 0.680, rustField);
        const rustEdge = smoothstep(0.465, 0.505, rustField) * (1 - rust);
        const sc = scratch[i];
        // metal exposto por DOIS caminhos: o risco fundo e a tinta que
        // saltou em placa. O segundo garante que a chapa leia como metal
        // mesmo onde o risco e fraco - senao vira plastico cinza.
        const scraped = smoothstep(0.42, 0.85, sc) * (0.45 + nm * 0.8);
        const flaked = smoothstep(0.74, 0.93, nm) * (0.5 + nf * 0.7);
        const bare = clamp01(Math.max(scraped, flaked)) * (1 - rust);

        let base = 1 + (nm - 0.5) * 0.16 + (nu - 0.5) * 0.07;
        let r = PR * base, g = PG * base, b = PB * base;
        // casca de laranja da pintura industrial
        r += (nf - 0.5) * 0.014; g += (nf - 0.5) * 0.014; b += (nf - 0.5) * 0.014;

        // ferrugem: laranja terroso, granulada
        const rr = 0.330 + (nf - 0.5) * 0.13, rg = 0.152 + (nf - 0.5) * 0.07, rb = 0.075 + (nf - 0.5) * 0.03;
        r = lerp(r, rr, rust); g = lerp(g, rg, rust); b = lerp(b, rb, rust);
        r += rustEdge * 0.055; g += rustEdge * 0.022; b += rustEdge * 0.005;

        // metal cru raspado: cinza claro levemente azulado
        r = lerp(r, 0.560 + sc * 0.12, bare);
        g = lerp(g, 0.575 + sc * 0.12, bare);
        b = lerp(b, 0.600 + sc * 0.12, bare);

        const grime = bottom * (0.4 + nf * 0.8);
        r -= grime * 0.045; g -= grime * 0.048; b -= grime * 0.050;

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        // amassados largos + crosta de ferrugem levantando + sulco do risco
        const pit = (1 - smoothstep(0.05, 0.55, F.vFine.d1[i])) * rust;
        const peel = (1 - smoothstep(0.10, 0.90, F.vFine.d1[i])) * (1 - rust);
        hgt[i] = 0.62 + (nl - 0.5) * 0.16 + (nm - 0.5) * 0.07 + rust * 0.18 * nf
               - pit * 0.42 + peel * 0.13 - sc * 0.16 + (nf - 0.5) * 0.05 + (nu - 0.5) * 0.06;
        rgh[i] = lerp(
          lerp(0.545 + (nu - 0.5) * 0.10, 0.940 + (nf - 0.5) * 0.06, rust),
          0.330 + (nu - 0.5) * 0.10, bare
        ) + grime * 0.05;
        // ferrugem NAO e metal; a tinta tambem nao. So o que raspou.
        mtl[i] = bare * 0.95;
      }
    }
    this._finish('metal', 'metal', {
      height: 0.011, aoRadius: 6, aoStrength: 0.70, normalScale: 1.0, metalnessMap: true
    });
  }

  /** Telha metalica ondulada galvanizada, com ferrugem escorrendo. */
  _defMetalThin() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    const scratch = channelField(
      drawStrokes(S, 200, mulberry32(0x33aa77), {
        min: 10, max: 120, width: 0.8, bend: 6,
        angle: (r) => (r() < 0.5 ? 0.06 : Math.PI - 0.06) + (r() - 0.5) * 0.25,
        alpha: (r) => 0.15 + r() * 0.5
      }), S, 0);

    // perfil trapezoidal pre-calculado por coluna: 10 ondas fechadas no tile
    const WAVES = 10;
    const corr = new Float32Array(S);
    for (let x = 0; x < S; x++) {
      const p = ((x * WAVES) / S) % 1;
      const t = p < 0.5 ? p * 2 : (1 - p) * 2;
      corr[x] = smoothstep(0.14, 0.86, t);
    }

    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 83) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + 137) & M);
        const nl = F.low[ia], nf = F.fine[i], nu = F.micro[i];
        const streak = F.streak[i];
        const wave = corr[x];
        // encardido por ruido (ver nota 6 do cabecalho)
        const bottom = smoothstep(0.46, 0.84, F.low[i]);

        // ferrugem prefere o vale da onda, onde a agua fica parada
        const rustField = nl * 0.55 + smoothstep(0.45, 0.92, streak) * 0.35
                        + bottom * 0.24 + (1 - wave) * 0.13;
        const rust = smoothstep(0.560, 0.735, rustField);
        const spangle = 0.5 + (F.vMid.id[i] - 0.5) * 0.55;   // cristal do zinco

        let v = 0.520 + spangle * 0.085 + (nf - 0.5) * 0.05 + wave * 0.055;
        let r = v * 0.995, g = v * 1.0, b = v * 1.02;

        const rr = 0.340 + (nf - 0.5) * 0.14, rg = 0.160 + (nf - 0.5) * 0.07, rb = 0.082;
        r = lerp(r, rr, rust); g = lerp(g, rg, rust); b = lerp(b, rb, rust);

        const sc = scratch[i] * 0.5;
        r += sc * 0.08; g += sc * 0.08; b += sc * 0.08;
        const grime = bottom * (0.35 + nf * 0.8);
        r -= grime * 0.055; g -= grime * 0.058; b -= grime * 0.058;

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        hgt[i] = 0.15 + wave * 0.72 + (nl - 0.5) * 0.05 + rust * 0.05 * nf + (nu - 0.5) * 0.02;
        rgh[i] = Math.max(0.45, lerp(0.470 + (nu - 0.5) * 0.10 + grime * 0.10 - sc * 0.06,
                      0.940 + (nf - 0.5) * 0.06, rust));
        mtl[i] = (1 - rust) * 0.92;
      }
    }
    this._finish('metal_thin', 'metal_thin', {
      height: 0.045, aoRadius: 7, aoStrength: 0.60, normalScale: 1.0,
      metalnessMap: true, side: THREE.DoubleSide
    });
  }

  /** Tabua com veio de verdade, nos, junta de topo e borda gasta. */
  _defWood() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;

    const PLANKS = 4;
    const pw = S / PLANKS;
    // nos pre-sorteados: distancia por pixel a ate 6 nos ainda e barato
    const knots = [];
    for (let p = 0; p < PLANKS; p++) {
      const n = hash2i(p, 0, 5) > 0.45 ? 2 : 1;
      for (let k = 0; k < n; k++) {
        knots.push({
          x: (p + 0.22 + hash2i(p, k, 71) * 0.56) * pw,
          y: (0.08 + hash2i(p, k, 131) * 0.84) * S,
          r: 11 + hash2i(p, k, 211) * 13
        });
      }
    }

    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 19) & M) * S;
      const v = y / S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const p = Math.floor(x / pw);
        const u = x / pw - p;
        const ph = hash2i(p, 0, 13);

        // junta de topo, uma por tabua, em altura propria
        const jv = 0.16 + hash2i(p, 0, 29) * 0.66;
        let dj = Math.abs(v - jv);
        if (dj > 0.5) dj = 1 - dj;
        const gapPx = Math.min(Math.min(u, 1 - u) * pw, dj * S);
        const face = smoothstep(1.4, 4.6, gapPx);

        // no: escurece, endurece e curva o veio ao redor
        let knot = 0, knotRing = 0;
        for (let k = 0; k < knots.length; k++) {
          const kn = knots[k];
          let dx = x - kn.x;
          if (dx > S * 0.5) dx -= S; else if (dx < -S * 0.5) dx += S;
          let dy = y - kn.y;
          if (dy > S * 0.5) dy -= S; else if (dy < -S * 0.5) dy += S;
          const dist = Math.sqrt(dx * dx + dy * dy * 0.55);   // no e ovalado
          const kv = smoothstep(kn.r, kn.r * 0.25, dist);
          if (kv > knot) {
            knot = kv;
            knotRing = 0.5 + 0.5 * Math.sin(dist * 0.85);
          }
        }

        // veio: aneis atravessando a largura, ondulando ao longo do comprimento.
        // A densidade de anel e sorteada POR TABUA - com um valor unico as
        // quatro tabuas saiam com o mesmo desenho, que le como padrao gerado.
        const wander = (F.streak[rowA + ((x + 61) & M)] - 0.5) * 1.35;
        const t = u * (4.5 + hash2i(p, 2, 89) * 4.0) + ph * 4.0 + wander + knot * 2.6;
        const ring = t - Math.floor(t);
        const line = smoothstep(0.50, 0.94, Math.abs(ring * 2 - 1));
        const fiber = (F.streak[i] - 0.5);

        // cor base da tabua: de mel claro a nogueira escura
        const tone = hash2i(p, 1, 37);
        let br = 0.360 + tone * 0.170;
        let bg = 0.238 + tone * 0.115;
        let bb = 0.148 + tone * 0.070;
        // veio escuro e fibra longitudinal
        const dark = line * (0.30 + tone * 0.20) + Math.max(0, fiber) * 0.16;
        br *= 1 - dark * 0.70; bg *= 1 - dark * 0.74; bb *= 1 - dark * 0.76;
        // no bem mais escuro
        br = lerp(br, 0.150 + knotRing * 0.075, knot * 0.88);
        bg = lerp(bg, 0.092 + knotRing * 0.045, knot * 0.88);
        bb = lerp(bb, 0.058 + knotRing * 0.028, knot * 0.88);
        // madeira exposta ao tempo acinzenta, principalmente nas bordas
        const grey = smoothstep(0.55, 0.0, Math.min(u, 1 - u) * 2) * 0.55
                   + smoothstep(0.6, 0.95, F.low[i]) * 0.45;
        br = lerp(br, 0.335, grey * 0.42);
        bg = lerp(bg, 0.320, grey * 0.42);
        bb = lerp(bb, 0.298, grey * 0.42);

        // Fresta entre tabuas: e MADEIRA EM SOMBRA, nao tinta preta. O 0.055
        // chapado que estava aqui lia como traco de caneta entre blocos marrons;
        // agora o escurecimento vem do AO (aoStrength subiu junto) e da altura.
        const r = lerp(br * 0.22, br, face);
        const g = lerp(bg * 0.22, bg, face);
        const b = lerp(bb * 0.24, bb, face);

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        // o veio de primavera erode antes: a linha escura fica RECUADA
        hgt[i] = lerp(0.06, 0.78 - line * 0.14 - Math.max(0, fiber) * 0.08 + knot * 0.06
                      + (F.micro[i] - 0.5) * 0.05, face);
        rgh[i] = lerp(0.960, 0.800 + line * 0.075 + grey * 0.075 - knot * 0.12
                      + (F.micro[i] - 0.5) * 0.07, face);
        mtl[i] = 0;
      }
    }
    this._finish('wood', 'wood', {
      height: 0.006, aoRadius: 5, aoStrength: 0.95, normalScale: 1.0
    });
  }

  /** Vidro sujo e riscado. Transmissao de verdade (MeshPhysicalMaterial). */
  _defGlass() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    const scratch = channelField(
      drawStrokes(S, 130, mulberry32(0xbeef01), {
        min: 14, max: 150, width: 0.7, bend: 20,
        alpha: (r) => 0.12 + r() * 0.35
      }), S, 0);

    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 211) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + 43) & M);
        const nl = F.low[ia], nf = F.fine[i];
        const streak = F.streak[i];

        // Poeira em mancha + marca de chuva escorrida. Nada de sujeira presa a
        // borda da imagem: alem de nao fechar, a borda vira um vinco no normal
        // map exatamente na emenda do tile.
        const dustGate = smoothstep(0.55, 0.85, F.low[i]);
        const dust = clamp01((smoothstep(0.48, 0.84, nl) * 0.52
                   + smoothstep(0.58, 0.92, streak) * 0.48) * dustGate);
        const sc = scratch[i];

        const v = 0.550 + dust * 0.095 + sc * 0.035;
        const c = i * 3;
        col[c] = v * 0.965;
        col[c + 1] = v;
        col[c + 2] = v * 0.975;                 // vidro comum puxa verde
        hgt[i] = 0.5 + (nl - 0.5) * 0.10 + dust * 0.06 * nf - sc * 0.05;
        // liso quase perfeito, embacando onde tem crosta de sujeira
        rgh[i] = clamp01(0.045 + dust * 0.34 + sc * 0.30 + (F.micro[i] - 0.5) * 0.02);
        mtl[i] = 0;
      }
    }
    this._finish('glass', 'glass', {
      height: 0.0035, aoRadius: 6, aoStrength: 0.25, normalScale: 1.0,
      physical: true, side: THREE.DoubleSide, aoMap: false,
      params: {
        transmission: 0.94,
        thickness: 0.012,
        ior: 1.52,
        transparent: false,
        specularIntensity: 1.0,
        envMapIntensity: 1.6,
        color: new THREE.Color(0.92, 0.97, 0.94)
      }
    });
  }

  /** Terra batida: torrao, pedrisco solto, trinca de seca, poca umida. */
  _defDirt() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 307) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + 131) & M);
        const nl = F.low[ia], nm = F.mid[i], nf = F.fine[i], nu = F.micro[i];

        const damp = smoothstep(0.58, 0.86, nl);
        // Barro seco e o UNICO caso em que a rede fechada esta certa: a lama
        // racha em placas poligonais. Entao aqui a fonte e a FRONTEIRA do
        // voronoi (segmento reto, juncao em Y, placa de ~7 cm) - e nao a
        // isolinha de um ruido, que dava laco redondo de largura constante.
        const crack = smoothstep(0.11, 0.0, F.vBig.d2[i]) * (1 - damp)
                    * smoothstep(0.38, 0.68, nm);
        // pedrisco: so as celulas sorteadas viram pedra
        const isStone = F.vFine.id[i] > 0.80;
        const stone = isStone ? smoothstep(0.55, 0.18, F.vFine.d1[i]) : 0;
        const clod = smoothstep(0.40, 0.75, nm);

        let r = 0.330 + (nl - 0.5) * 0.100 + (nf - 0.5) * 0.055 + clod * 0.045;
        let g = 0.252 + (nl - 0.5) * 0.082 + (nf - 0.5) * 0.045 + clod * 0.035;
        let b = 0.172 + (nl - 0.5) * 0.062 + (nf - 0.5) * 0.032 + clod * 0.022;

        // pedra cinza, dessaturada
        const sv = 0.42 + F.vFine.id[i] * 0.20 + (nu - 0.5) * 0.08;
        r = lerp(r, sv, stone * 0.85);
        g = lerp(g, sv * 0.985, stone * 0.85);
        b = lerp(b, sv * 0.945, stone * 0.85);

        // solo umido escurece MUITO e reflete um pouco
        r *= 1 - damp * 0.34; g *= 1 - damp * 0.33; b *= 1 - damp * 0.28;
        const cd = crack * 0.07;
        r -= cd; g -= cd; b -= cd;

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        hgt[i] = 0.48 + (nm - 0.5) * 0.26 + (nf - 0.5) * 0.14 + stone * 0.22
               - crack * 0.30 + (nu - 0.5) * 0.06;
        rgh[i] = 0.945 + (nu - 0.5) * 0.07 - damp * 0.30 - stone * 0.10;
        mtl[i] = 0;
      }
    }
    this._finish('dirt', 'dirt', {
      height: 0.014, aoRadius: 7, aoStrength: 0.85, normalScale: 1.0
    });
  }

  /** Areia: marca de vento, grao brilhando, sombra no vale da marola. */
  _defSand() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    // rastro de pe e de arrasto: e o que quebra a leitura de tecido
    const scuffF = channelField(
      drawStrokes(S, 160, mulberry32(0x5a4d21), {
        min: 24, max: 88, width: 4.0, bend: 12,
        alpha: (r) => 0.25 + r() * 0.55
      }), S, 0);

    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 401) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + 289) & M);
        const nl = F.low[ia], nf = F.fine[i], nu = F.micro[i], w = F.warp[i];
        const scuff = scuffF[i];

        // TRES trens de onda, cada um com direcao, periodo e peso proprios (os
        // coeficientes seguem inteiros, entao cada um fecha no tile). Uma unica
        // senoide de direcao fixa e amplitude constante - que era o que havia
        // aqui - le como veludo cotele, nao como duna. O warp tambem subiu, para
        // as cristas entortarem em vez de so deslizarem de fase.
        const dirMix = smoothstep(0.28, 0.72, F.low[i]);
        const w1a = Math.sin(((x * 7 + y * 3) * TAU) / S + (w - 0.5) * 8.0);
        const w1b = Math.sin(((x * -4 + y * 9) * TAU) / S + (F.mid[i] - 0.5) * 7.0);
        const w1 = lerp(w1a, w1b, dirMix);
        const w2 = Math.sin(((x * 3 - y * 8) * TAU) / S + (F.mid[i] - 0.5) * 7.0);
        const w3 = Math.sin(((x * 13 + y * 5) * TAU) / S + (nl - 0.5) * 6.0);
        const wav = (w1 + w2 * 0.48 + w3 * 0.25) / 1.73;
        const ripple = clamp01(wav * 0.5 + 0.5);
        let rip = ripple * ripple * (3 - 2 * ripple);
        // a marola morre em manchas (areia pisada) em vez de cobrir tudo
        rip *= smoothstep(0.30, 0.70, F.low[i]) * (1 - scuff * 0.92);
        const grain = F.white[i];
        const damp = smoothstep(0.58, 0.88, F.mid[i]);   // areia umida/compactada

        let v = 0.660 + (nl - 0.5) * 0.085 + rip * 0.075 + (nf - 0.5) * 0.035
              - damp * 0.055 - scuff * 0.030;
        let r = v * 1.055, g = v * 0.955, b = v * 0.735;   // areia quente
        // vale da marola junta poeira mais escura
        const trough = (1 - rip) * 0.06;
        r -= trough; g -= trough * 1.05; b -= trough * 1.1;
        // graozinho de quartzo pegando luz
        const spark = grain > 0.985 ? 0.16 : 0;
        r += spark; g += spark; b += spark * 0.9;

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        const gbump = 1 - smoothstep(0.10, 0.80, F.vFine.d1[i]);
        hgt[i] = 0.45 + rip * 0.22 + (nl - 0.5) * 0.13 + (nf - 0.5) * 0.12
               + (nu - 0.5) * 0.10 + gbump * 0.16 - scuff * 0.07;
        // a mancha de areia umida do albedo TEM que aparecer aqui tambem, senao
        // le como tinta: areia compactada e bem menos aspera que areia solta
        rgh[i] = 0.925 + (nu - 0.5) * 0.055 - spark * 0.35 - damp * 0.20;
        mtl[i] = 0;
      }
    }
    this._finish('sand', 'sand', {
      height: 0.021, aoRadius: 6, aoStrength: 0.55, normalScale: 1.0
    });
  }

  /** Brita/pedrisco: pedras de dois tamanhos, fresta funda entre elas. */
  _defGravel() {
    const S = this.size, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    for (let y = 0; y < S; y++) {
      const row = y * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const nf = F.fine[i], nu = F.micro[i], nl = F.low[i];

        const dBig = F.vBig.d1[i], idBig = F.vBig.id[i];
        const dMid = F.vMid.d1[i], idMid = F.vMid.id[i];
        const dFin = F.vFine.d1[i], idFin = F.vFine.id[i];
        // Silhueta: mascara radial sobre d1 devolve pedra CIRCULAR, e circulo
        // perfeito entrega voronoi de longe. Deformar o raio por ruido quebra a
        // borda sem precisar de distancia por eixo.
        const warpR = 1 + (F.mid[i] - 0.5) * 0.55 + (nf - 0.5) * 0.30;
        // tamanho POR PEDRA (cauda longa): hash independente do id, senao pedra
        // grande seria sempre pedra clara. Antes toda pedra tinha o mesmo raio.
        const szB = 0.50 + hash2i((idBig * 8192) | 0, 3, 77) * 1.00;
        const szM = 0.60 + hash2i((idMid * 8192) | 0, 9, 131) * 0.85;
        // poucas pedras grandes DESTACANDO (era 58% das celulas: com quase tudo
        // ocupado nas duas escalas o chao virava empacotamento de seixo igual)
        const bigOn = idBig > 0.74 ? 1 : 0;
        const midOn = idMid > 0.55 ? 1 : 0;
        const big = bigOn * smoothstep(0.80 * szB, 0.24 * szB, dBig * warpR);
        const small = midOn * smoothstep(0.82 * szM, 0.28 * szM, dMid * warpR);
        const chip = smoothstep(0.66, 0.24, dFin * warpR);   // terceira escala
        const smallE = small * (1 - big * 0.75);
        const chipE = chip * 0.18 * (1 - big) * (1 - small);
        const stone = clamp01(Math.max(big, Math.max(smallE, chipE)));
        const id = big > smallE ? idBig : (smallE > chipE ? idMid : idFin);

        // cinza britado com algumas pedras quentes (basalto x granito)
        const warm = id > 0.78 ? 1 : 0;
        let v = 0.335 + id * 0.245 + (nf - 0.5) * 0.075 + (nl - 0.5) * 0.05;
        v = lerp(v * 0.72, v, stone);          // fresta escura entre pedras
        let r = v * (warm ? 1.09 : 1.0);
        let g = v * (warm ? 0.965 : 0.995);
        let b = v * (warm ? 0.855 : 0.975);

        // po de pedra assentado nas frestas: areas LISAS que quebram a
        // granulacao uniforme (com 0.5 quase nao aparecia po nenhum)
        const dust = (1 - stone) * 0.8;
        r += dust * 0.100; g += dust * 0.094; b += dust * 0.086;

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        hgt[i] = 0.16 + big * 0.55 + smallE * 0.30 + chipE * 0.14
               + (nf - 0.5) * 0.10 + (nu - 0.5) * 0.06;
        // pedra rolada e menos aspera que o po entre elas
        rgh[i] = 0.965 - stone * 0.20 + (nu - 0.5) * 0.08 + (id > 0.9 ? -0.10 : 0);
        mtl[i] = 0;
      }
    }
    this._finish('gravel', 'gravel', {
      height: 0.018, aoRadius: 6, aoStrength: 0.95, normalScale: 1.0
    });
  }

  /** Mato urbano: touceira verde, falha de terra, palha seca. */
  _defGrass() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    const rngG = mulberry32(0x6a5501);
    const blades = drawStrokes(S, 3600, rngG, {
      min: 5, max: 15, width: 1.15, bend: 5,
      // folhas apontam para cima na textura (y menor), com abertura
      angle: (r) => -Math.PI / 2 + (r() - 0.5) * 1.5,
      color: (r) => `rgb(255,${(60 + r() * 195) | 0},${(40 + r() * 215) | 0})`
    });

    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 173) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const j = i * 4;
        const ia = rowA + ((x + 211) & M);
        const nl = F.low[ia], nm = F.mid[i], nf = F.fine[i];

        // base: terra aparecendo onde o mato falhou
        const cover = smoothstep(0.34, 0.60, nl * 0.75 + nm * 0.35);
        const dry = smoothstep(0.52, 0.82, F.low[i]);       // palha seca
        let r = lerp(0.300, lerp(0.165, 0.360, dry), cover);
        let g = lerp(0.228, lerp(0.238, 0.335, dry), cover);
        let b = lerp(0.152, lerp(0.086, 0.150, dry), cover);
        r += (nf - 0.5) * 0.05; g += (nf - 0.5) * 0.05; b += (nf - 0.5) * 0.03;

        // folhas rasterizadas por cima
        const covA = blades[j] / 255;
        const bladeCov = covA * (0.35 + cover * 0.75);
        if (bladeCov > 0.004) {
          const tone = covA > 0.03 ? blades[j + 1] / (blades[j] || 1) : 0.5;
          const dep = covA > 0.03 ? blades[j + 2] / (blades[j] || 1) : 0.5;
          const lit = 0.55 + dep * 0.55;                    // folha de cima pega luz
          const br = lerp(0.135, 0.395, tone * dry + tone * 0.35) * lit;
          const bg = lerp(0.230, 0.415, tone * 0.85) * lit;
          const bb = lerp(0.075, 0.150, tone) * lit;
          r = lerp(r, br, bladeCov);
          g = lerp(g, bg, bladeCov);
          b = lerp(b, bb, bladeCov);
        }

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        const tuft = smoothstep(0.85, 0.15, F.vMid.d1[i]) * cover;
        hgt[i] = 0.30 + cover * 0.14 + tuft * 0.30 + bladeCov * 0.42
               + (nm - 0.5) * 0.16 + (nf - 0.5) * 0.14 + (F.micro[i] - 0.5) * 0.08;
        rgh[i] = 0.885 - bladeCov * 0.12 + (F.micro[i] - 0.5) * 0.08 + (1 - cover) * 0.05;
        mtl[i] = 0;
      }
    }
    this._finish('grass', 'grass', {
      height: 0.017, aoRadius: 6, aoStrength: 0.90, normalScale: 1.0
    });
  }

  /** Lona/toldo: trama de fio grosso, vinco, listra desbotada, limo embaixo. */
  _defFabric() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;

    const THREADS = 128;                    // 128 fios por metro: lona grossa
    const per = S / THREADS;
    const idxA = new Int32Array(S);
    const bumpA = new Float32Array(S);
    for (let x = 0; x < S; x++) {
      const p = x / per;
      idxA[x] = Math.floor(p);
      bumpA[x] = Math.sin((p - Math.floor(p)) * Math.PI);
    }

    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 97) & M) * S;
      const iy = idxA[y], by = bumpA[y];
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + 313) & M);
        const nl = F.low[ia], nf = F.fine[i], nu = F.micro[i];
        // encardido e desbotado por ruido (ver nota 6 do cabecalho)
        const bottom = smoothstep(0.48, 0.82, F.low[i]);
        const sun = smoothstep(0.52, 0.86, F.mid[i]);

        // tafeta: par sobe o fio vertical, impar sobe o horizontal
        const over = ((idxA[x] + iy) & 1) === 0;
        const hWarp = bumpA[x] * (over ? 1.0 : 0.42);
        const hWeft = by * (over ? 0.42 : 1.0);
        const weave = Math.max(hWarp, hWeft);

        // Listra desbotada e deformada, sem a borda binaria de papel estampado.
        const stripe = Math.sin((x * TAU * 2) / S + (F.low[i] - 0.5) * 1.4);
        const band = smoothstep(-0.22, 0.22, stripe);
        let r = lerp(0.350, 0.255, band);
        let g = lerp(0.352, 0.292, band);
        let b = lerp(0.305, 0.235, band);
        const fade = 1 + sun * 0.13 + (nl - 0.5) * 0.14;
        r *= fade; g *= fade; b *= fade;
        // fio a fio: a trama alterna luz e sombra
        const shade = 0.80 + weave * 0.30 + (over ? 0.03 : -0.03);
        r *= shade; g *= shade; b *= shade;
        r += (nu - 0.5) * 0.02; g += (nu - 0.5) * 0.02; b += (nu - 0.5) * 0.02;

        // limo/mofo esverdeado subindo pela barra
        const mold = bottom * smoothstep(0.35, 0.85, F.mid[i]);
        r -= mold * 0.085; g -= mold * 0.055; b -= mold * 0.080;
        // fiapo solto
        const fuzz = smoothstep(0.86, 1.0, nf) * 0.10;
        r += fuzz; g += fuzz; b += fuzz;

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        // vinco largo (a lona nunca fica esticada) somado a trama
        const crease = (F.warp[i] - 0.5) * 0.30 + (F.crack[i] - 0.5) * 0.16;
        hgt[i] = 0.42 + weave * 0.30 + crease + (nf - 0.5) * 0.05;
        rgh[i] = 0.930 + (nu - 0.5) * 0.07 - weave * 0.05 + mold * 0.03;
        mtl[i] = 0;
      }
    }
    this._finish('fabric', 'fabric', {
      height: 0.006, aoRadius: 5, aoStrength: 0.85, normalScale: 1.0,
      physical: true, side: THREE.DoubleSide,
      params: { transmission: 0.12, thickness: 0.008, ior: 1.38, sheen: 0.32, sheenRoughness: 0.78 }
    });
  }

  /** Borracha de pneu: banda de rodagem com sulco, friso e desgaste. */
  _defRubber() {
    const S = this.size, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    const RIBS = 3, LAT = 7;
    for (let y = 0; y < S; y++) {
      const row = y * S;
      const v = y / S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const u = x / S;
        const nf = F.fine[i], nu = F.micro[i], nl = F.low[i];

        // sulcos longitudinais (3 ribs) - distancia ate o sulco mais proximo
        let dLong = 1;
        for (let k = 0; k < RIBS; k++) {
          const cpos = (k + 0.5) / RIBS;
          let d = Math.abs(u - cpos);
          if (d > 0.5) d = 1 - d;
          if (d < dLong) dLong = d;
        }
        // sulcos transversais inclinados: 1 periodo em x mantem o tile fechado
        const lat = (v * LAT + u) % 1;
        const dLat = Math.min(lat, 1 - lat);
        // friso fino (sipe) dentro do bloco - coeficientes INTEIROS, senao a
        // borda do tile nao fecha e aparece uma emenda no pneu
        const sipe = (v * LAT * 3 + u * 3) % 1;
        const dSipe = Math.min(sipe, 1 - sipe);

        const grooveL = smoothstep(0.052, 0.028, dLong);
        const grooveT = smoothstep(0.085, 0.045, dLat);
        const groove = Math.max(grooveL, grooveT);
        const sipeMask = smoothstep(0.030, 0.012, dSipe) * (1 - groove);

        // borracha e quase preta: o que a define e o relevo e o brilho, nao a cor
        let v0 = 0.132 + (nl - 0.5) * 0.030 + (nf - 0.5) * 0.022;
        // topo do bloco fica polido e empoeirado pelo asfalto
        const crown = (1 - groove) * smoothstep(0.35, 0.75, nl);
        v0 += crown * 0.048;
        v0 -= groove * 0.032;
        const r = v0 * 1.0, g = v0 * 0.99, b = v0 * 1.03;

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        hgt[i] = 0.82 - groove * 0.66 - sipeMask * 0.26 + (nf - 0.5) * 0.05 + (nu - 0.5) * 0.04;
        rgh[i] = 0.760 - crown * 0.20 + groove * 0.14 + (nu - 0.5) * 0.09;
        mtl[i] = 0;
      }
    }
    this._finish('rubber', 'rubber', {
      height: 0.010, aoRadius: 5, aoStrength: 0.95, normalScale: 1.0
    });
  }

  /** Saco de areia: aniagem grossa, barriga cheia, base encardida. */
  _defSandbag() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;

    const THREADS = 64;                     // fio grosso de juta (~1 cm)
    const per = S / THREADS;
    const idxA = new Int32Array(S);
    const bumpA = new Float32Array(S);
    for (let x = 0; x < S; x++) {
      const p = x / per;
      idxA[x] = Math.floor(p);
      bumpA[x] = Math.sin((p - Math.floor(p)) * Math.PI);
    }

    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 251) & M) * S;
      const iy = idxA[y], by = bumpA[y];
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + 59) & M);
        const nl = F.low[ia], nm = F.mid[i], nf = F.fine[i], nu = F.micro[i];
        // encardido e desbotado por ruido (ver nota 6 do cabecalho)
        const bottom = smoothstep(0.46, 0.80, F.low[i]);
        const sun = smoothstep(0.52, 0.86, nm);

        const over = ((idxA[x] + iy) & 1) === 0;
        const hWarp = bumpA[x] * (over ? 1.0 : 0.40);
        const hWeft = by * (over ? 0.40 : 1.0);
        const weave = Math.max(hWarp, hWeft);

        // caqui de juta, variando fio a fio (juta nunca tem cor uniforme)
        const fiber = hash2i(idxA[x], iy, 23);
        let base = 0.520 + fiber * 0.105 + (nl - 0.5) * 0.115;
        base += sun * 0.055;
        let r = base * 1.085, g = base * 0.965, b = base * 0.700;
        const shade = 0.78 + weave * 0.34;
        r *= shade; g *= shade; b *= shade;

        // barriga: a areia dentro estufa o pano em ondas largas
        const belly = (nm - 0.5) * 0.55 + (F.warp[i] - 0.5) * 0.30;

        const grime = bottom * (0.45 + nf * 0.8);
        r -= grime * 0.092; g -= grime * 0.085; b -= grime * 0.068;
        const fuzz = smoothstep(0.88, 1.0, nf) * 0.09;
        r += fuzz; g += fuzz; b += fuzz;

        const c = i * 3;
        col[c] = r; col[c + 1] = g; col[c + 2] = b;
        hgt[i] = 0.45 + weave * 0.26 + belly * 0.30 + (nf - 0.5) * 0.06;
        rgh[i] = 0.955 + (nu - 0.5) * 0.06 - weave * 0.04 + grime * 0.02;
        mtl[i] = 0;
      }
    }
    this._finish('sandbag', 'sandbag', {
      height: 0.009, aoRadius: 6, aoStrength: 0.85, normalScale: 1.0
    });
  }

  /** Poca de agua parada: escura, muito lisa, com marola minima e borda suja. */
  _defWater() {
    const S = this.size, M = S - 1, F = this._fields;
    const { col, hgt, rgh, mtl } = this._scratch;
    for (let y = 0; y < S; y++) {
      const row = y * S;
      const rowA = ((y + 149) & M) * S;
      for (let x = 0; x < S; x++) {
        const i = row + x;
        const ia = rowA + ((x + 71) & M);
        const nl = F.low[ia], nf = F.fine[i];

        // duas ondas cruzadas com periodo inteiro: marola sem costura
        const w1 = Math.sin(((x * 5 + y * 2) * TAU) / S + (F.warp[i] - 0.5) * 3.0);
        const w2 = Math.sin(((x * -3 + y * 6) * TAU) / S + (F.mid[i] - 0.5) * 4.0);
        const ripple = (w1 * 0.6 + w2 * 0.4) * 0.5 + 0.5;

        // fundo escuro; a poca aparece pelo reflexo, nao pela cor
        const silt = smoothstep(0.45, 0.85, nl);
        const v = 0.098 + silt * 0.070 + (nf - 0.5) * 0.022;
        const c = i * 3;
        col[c] = v * 1.0;
        col[c + 1] = v * 1.06;
        col[c + 2] = v * 1.12;
        hgt[i] = 0.5 + (ripple - 0.5) * 0.5 + (nl - 0.5) * 0.1;
        rgh[i] = clamp01(0.055 + silt * 0.20 + (F.micro[i] - 0.5) * 0.03);
        mtl[i] = 0;
      }
    }
    this._finish('water', 'water', {
      height: 0.0060, aoRadius: 8, aoStrength: 0.30, normalScale: 1.0,
      params: { envMapIntensity: 1.35 }
    });
  }
}
